<template>
  <div
    class="profile-container"
    :class="{ 'high-contrast-mode': highContrastMode }"
  >
    <!-- Animation de déblocage du badge -->
    <div v-if="showBadgeUnlockAnimation" class="badge-unlock-overlay">
      <div class="badge-unlock-animation">
        <div class="badge-icon" v-if="newlyUnlockedBadge">
          <img
            v-if="shouldUseBadgeImage(newlyUnlockedBadge.id)"
            :src="getBadgeImagePath(newlyUnlockedBadge.id)"
            :alt="newlyUnlockedBadge.title"
            :class="[
              'badge-unlock-image',
              {
                'badge-image-enlarged': shouldEnlargeBadgeImage(
                  newlyUnlockedBadge.id,
                ),
                'badge-image-formation': isFormationBadge(
                  newlyUnlockedBadge.id,
                ),
              },
            ]"
          />
          <span v-else>{{ newlyUnlockedBadge.icon }}</span>
        </div>
        <h2>Badge débloqué !</h2>
        <h3>{{ newlyUnlockedBadge.title }}</h3>
        <p>{{ newlyUnlockedBadge.description }}</p>
        <button @click="closeBadgeAnimation" class="close-animation-btn">
          Continuer
        </button>
      </div>
    </div>

    <!-- Guide Avatar pour guider l'utilisateur -->
    <!--
    <guide-avatar
      guide-name="Flamou"
      :forced-message="guideMessage"
      :forced-options="guideOptions"
      :force-show-message="guideForceShow"
      :context="guideContext"
      :auto-show-delay="0"
      :custom-position="guidePosition"
      :profile-tour-completed="profileTourCompleted"
      :dashboard-visited="!isFirstVisit"
      class="guide-top-left"
      @option-selected="handleGuideOptionSelected"
      @guide-opened="prepareGuideForRediscovery"
      @guide-dismissed="resetGuideState"
      @restart-profile-tour="restartProfileTour"
    />
    -->

    <!-- Barre de navigation et titre -->
    <div class="profile-header">
      <h1 class="main-title">Mon Profil</h1>
      <p class="subtitle">Apprends à mieux te connaître !</p>
    </div>

    <!-- Menu de navigation -->
    <div class="navigation-tabs">
      <button
        v-for="(tab, index) in tabs"
        :key="index"
        @click="currentTab = tab.id"
        class="tab-button"
        type="button"
        :aria-pressed="currentTab === tab.id ? 'true' : 'false'"
        :class="{ active: currentTab === tab.id }"
      >
        <span class="tab-icon">{{ tab.icon }}</span>
        <span class="tab-label">{{ tab.name }}</span>
        <span
          v-if="tab.id === 'accompanists' && pendingInvitations.length > 0"
          class="tab-notification-badge"
        >
          {{ pendingInvitations.length }}
        </span>
      </button>
    </div>

    <!-- Contenu principal -->
    <div class="profile-content">
      <!-- Section Informations Personnelles -->
      <div v-if="currentTab === 'personal'" class="profile-section">
        <h2 class="section-title">
          <span class="section-icon">👤</span>
          Mes Informations
        </h2>

        <!-- Indicateur de chargement -->
        <div v-if="isLoadingUserProfile" class="loading-profile">
          <div class="loading-spinner"></div>
          <p>Chargement du profil...</p>
        </div>

        <!-- Message d'erreur -->
        <div v-if="userProfileError" class="profile-error">
          <p class="error-message">{{ userProfileError }}</p>
          <button
            @click="refreshUserProfile"
            class="refresh-profile-btn"
            :disabled="isLoadingUserProfile"
          >
            <span class="refresh-icon">🔄</span>
            Actualiser le profil
          </button>
        </div>

        <!-- Contenu du profil -->
        <div v-else class="personal-showcase">
          <!-- Carte d'Identité Principale -->
          <div class="identity-card-container">
            <div class="identity-card">
              <div class="card-header-bg"></div>
              
              <div class="identity-content">
                <div class="avatar-wrapper">
                  <AvatarDisplay
                    size="large"
                    custom-class="showcase-avatar"
                    alt-text="Avatar utilisateur"
                    picture-type="avatar"
                  />
                  <div class="level-bubble">
                    <span class="level-label">Niveau</span>
                    <span class="level-number">{{ calculateLevel() }}</span>
                  </div>
                </div>

                <div class="identity-details">
                  <h2 class="user-name">{{ userProfile.firstName }} <span v-if="userProfile.age" class="age-badge">{{ userProfile.age }} ans</span></h2>
                  
                  <div class="xp-container">
                     <div class="xp-bar-fancy">
                        <div class="xp-fill-fancy" :style="{ width: getExpPercentage() + '%' }"></div>
                     </div>
                     <span class="xp-text">{{ Math.round(getExpPercentage()) }}% vers niveau {{ calculateLevel() + 1 }}</span>
                  </div>

                  <div class="bio-bubble">
                    <p class="bio-text">{{ userProfile.bio || 'Ajoute une description de toi-même !' }}</p>
                    <button @click="showBioEditor = true" class="edit-bio-btn" title="Modifier">✏️</button>
                  </div>
                </div>
              </div>

              <!-- Badges des Loisirs (Stickers) -->
              <div class="hobbies-section">
                <h3 class="card-subtitle">Ce que j'aime</h3>
                <div class="stickers-container">
                  <div
                    v-for="(hobby, index) in userProfile.hobbies"
                    :key="index"
                    class="sticker-item"
                    :data-hobby="hobby"
                  >
                    {{ hobby }}
                  </div>
                  <button @click="showHobbyEditor = true" class="add-sticker-btn">+</button>
                </div>
              </div>
            </div>

            <!-- Carte Code de Liaison (Ticket) -->
            <div class="code-ticket" v-if="userProfile.youngCode">
              <div class="ticket-stub">
                <span class="ticket-icon">🔑</span>
              </div>
              <div class="ticket-body">
                <span class="ticket-label">MON CODE D'ÉQUIPE</span>
                <div class="ticket-code">{{ userProfile.youngCode }}</div>
                <button @click="copyCodeToClipboard" class="ticket-action" :class="{ 'copied': codeCopied }">
                  {{ codeCopied ? 'Copié !' : 'Copier' }}
                </button>
              </div>
              <div class="ticket-info">
                Partage ce code pour te connecter !
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Section Compétences (Mes Super Pouvoirs) -->
      <div v-if="currentTab === 'skills'" class="profile-section skills-showcase">
        <div class="section-header-gaming">
          <h2 class="gaming-title">
            <span class="gaming-icon">⚡</span>
            Mes Super Pouvoirs
          </h2>
        </div>

        <!-- Indicateur de chargement -->
        <div v-if="isLoadingSkills" class="loading-skills-modern">
          <div class="loading-spinner-modern"></div>
          <p>Analyse de tes capacités...</p>
        </div>

        <!-- Message d'erreur -->
        <div v-if="skillsError && !isLoadingSkills" class="skills-error-modern">
          <p class="error-message">{{ skillsError }}</p>
          <button @click="refreshSkills" class="retry-btn">
            Réessayer
          </button>
        </div>

        <div class="skills-dashboard" v-if="!isLoadingSkills && !skillsError">
          
          <!-- Top Stats (HUD) -->
          <div class="skills-hud">
            <div class="hud-item">
              <div class="hud-value">{{ skills.length }}</div>
              <div class="hud-label">Pouvoirs débloqués</div>
            </div>
            <div class="hud-item highlight">
              <div class="hud-value">{{ skills.filter((s) => s.level >= 4).length }}</div>
              <div class="hud-label">Maîtrises expertes</div>
            </div>
            <div class="hud-item">
              <div class="hud-value">{{ skillCategories.filter((cat) => getSkillsByCategory(cat.id).length > 0).length }}</div>
              <div class="hud-label">Domaines explorés</div>
            </div>
          </div>

          <!-- Liste des compétences par catégorie (Decks) -->
          <div class="skill-decks-container">
            <div
              v-for="(category, catIndex) in skillCategories"
              :key="catIndex"
              class="skill-deck"
              :class="'deck-' + category.id"
            >
              <h3 class="deck-title">
                <span class="deck-icon">{{ category.icon }}</span>
                {{ category.name }}
              </h3>

              <div class="deck-cards">
                <div
                  v-for="skill in getSkillsByCategory(category.id)"
                  :key="skill.id"
                  class="skill-card-modern"
                >
                  <div class="skill-card-icon" :style="{ backgroundColor: getLevelColor(skill.level) }">
                    {{ getSkillIcon(skill.id) }}
                  </div>
                  <div class="skill-card-content">
                    <div class="skill-card-header">
                      <span class="skill-name">{{ skill.name }}</span>
                      <span class="skill-source-tag">{{ skill.source === 'scenario' ? 'Jeu' : 'Quiz' }}</span>
                    </div>
                    
                    <div class="skill-level-bar-container">
                      <div class="skill-level-pips">
                        <span 
                          v-for="n in 5" 
                          :key="n" 
                          class="level-pip" 
                          :class="{ 'filled': n <= skill.level, 'max': skill.level === 5 }"
                          :style="{ backgroundColor: n <= skill.level ? getLevelColor(skill.level) : '' }"
                        ></span>
                      </div>
                      <span class="skill-level-text" :style="{ color: getLevelColor(skill.level) }">
                        {{ getSimpleSkillLevel(skill.level).replace(/<[^>]*>?/gm, '') }}
                      </span>
                    </div>
                  </div>
                </div>

                <div v-if="getSkillsByCategory(category.id).length === 0" class="empty-deck">
                  <span class="empty-deck-icon">🔒</span>
                  <p>Pouvoirs verrouillés</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Widget Vitesse de frappe (Speedometer) -->
          <div v-if="speedGameData.currentLevel" class="speed-widget-container">
            <h3 class="widget-title">🚀 Vitesse de Frappe</h3>
            <div class="speed-widget">
              <div class="speed-gauge">
                <div class="gauge-value">{{ getTypingSkillLevel().wpm }}</div>
                <div class="gauge-unit">Mots/min</div>
                <div class="gauge-circle" :style="{ '--percentage': Math.min(getTypingSkillLevel().wpm, 100) + '%' }"></div>
              </div>
              
              <div class="speed-details">
                <div class="speed-stat">
                  <span class="speed-icon">🎯</span>
                  <span class="speed-label">Précision</span>
                  <span class="speed-data">{{ getTypingSkillLevel().accuracy }}%</span>
                </div>
                <div class="speed-stat">
                  <span class="speed-icon">🏆</span>
                  <span class="speed-label">Niveau</span>
                  <span class="speed-data">{{ getTypingSkillLevel().level }}/5</span>
                </div>
                <div class="speed-message">
                  {{ getTypingSkillLevel().description }}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Section Environnement de Travail (Mon QG) -->
      <div v-if="currentTab === 'environment'" class="profile-section environment-showcase">
        <div class="section-header-gaming hq-theme">
          <h2 class="gaming-title">
            <span class="gaming-icon">🏗️</span>
            Mon QG Idéal
          </h2>
          <button
            @click="loadEnvironmentGameData"
            class="refresh-skills-btn-modern"
            :disabled="isLoadingEnvironmentData"
            title="Recalibrer les capteurs"
          >
            <span class="refresh-icon">🔄</span>
          </button>
        </div>

        <!-- Indicateur de chargement -->
        <div v-if="isLoadingEnvironmentData" class="loading-skills-modern">
          <div class="loading-spinner-modern"></div>
          <p>Scan de l'environnement...</p>
        </div>

        <!-- Message d'erreur -->
        <div
          v-if="environmentDataError && !isLoadingEnvironmentData"
          class="skills-error-modern"
        >
          <p class="error-message">{{ environmentDataError }}</p>
          <button
            @click="loadEnvironmentGameData"
            class="retry-btn"
          >
            Réessayer
          </button>
        </div>

        <div
          class="hq-dashboard"
          v-if="!isLoadingEnvironmentData && !environmentDataError"
        >
          <!-- Blueprint / Specs Card -->
          <div class="hq-specs-card" v-if="getEnvironmentPreferences().totalRoomsEvaluated > 0">
            <div class="specs-header">
              <h3>📋 Spécifications Techniques</h3>
              <span class="specs-status">OPTIMISÉ</span>
            </div>
            
            <div class="specs-grid">
              <div class="spec-item">
                <div class="spec-label">
                  <span class="spec-icon">💡</span> Éclairage
                </div>
                <div class="spec-value-box light-box">
                  {{ getEnvironmentPreferences().lightPreference }}
                </div>
              </div>

              <div class="spec-item">
                <div class="spec-label">
                  <span class="spec-icon">🔊</span> Sonore
                </div>
                <div class="spec-value-box noise-box">
                  {{ getEnvironmentPreferences().noisePreference }}
                </div>
              </div>

              <div class="spec-item">
                <div class="spec-label">
                  <span class="spec-icon">👥</span> Social
                </div>
                <div class="spec-value-box social-box">
                  {{ getEnvironmentPreferences().socialPreference }}
                </div>
              </div>

              <div class="spec-item">
                <div class="spec-label">
                  <span class="spec-icon">😌</span> Confort
                </div>
                <div class="spec-value-box comfort-box">
                  {{ getEnvironmentPreferences().overallComfort }}
                </div>
              </div>
            </div>
          </div>

          <!-- Unlocked Locations (Preferred Environments) -->
          <div
            v-if="getEnvironmentPreferences().preferredEnvironments.length > 0"
            class="hq-locations-section"
          >
            <h3 class="subsection-title-gaming">📍 Lieux Validés</h3>
            <div class="locations-grid">
              <div
                v-for="(env, index) in getEnvironmentPreferences().preferredEnvironments"
                :key="index"
                class="location-card"
                :class="'feedback-' + env.feedback.replace(' ', '-')"
              >
                <div class="location-icon-wrapper">
                  <span class="location-icon">🏢</span>
                </div>
                <div class="location-details">
                  <h4 class="location-name">{{ env.name }}</h4>
                  <div class="location-badges">
                    <span class="location-badge">{{ getFeedbackText(env.feedback) }}</span>
                  </div>
                  <div v-if="env.commentary" class="location-notes">
                    "{{ env.commentary }}"
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tactical Intel (Recommendations) -->
          <div
            class="hq-intel-section"
            v-if="getEnvironmentPreferences().totalRoomsEvaluated > 0"
          >
            <h3 class="subsection-title-gaming">📡 Données Tactiques</h3>
            <div class="intel-grid">
              <div class="intel-card">
                <div class="intel-icon">💡</div>
                <div class="intel-content">
                  <h4>Optimisation Lumière</h4>
                  <p>
                    Configuration recommandée : <strong>{{ getEnvironmentPreferences().lightPreference.toLowerCase() }}</strong> pour maximiser le focus.
                  </p>
                </div>
              </div>

              <div class="intel-card">
                <div class="intel-icon">🔊</div>
                <div class="intel-content">
                  <h4>Contrôle Audio</h4>
                  <p>
                    Protocole sonore : <strong>{{ getEnvironmentPreferences().noisePreference.toLowerCase() }}</strong> activé.
                  </p>
                </div>
              </div>

              <div class="intel-card">
                <div class="intel-icon">👥</div>
                <div class="intel-content">
                  <h4>Paramètres d'Équipe</h4>
                  <p>
                    Mode opératoire : <strong>{{ getEnvironmentPreferences().socialPreference.toLowerCase() }}</strong>.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- État vide -->
          <div
            v-if="getEnvironmentPreferences().totalRoomsEvaluated === 0"
            class="empty-deck"
          >
            <div class="empty-deck-icon">🗺️</div>
            <h3>Carte non explorée</h3>
            <p>
              Aucune donnée topographique. Lance la mission de reconnaissance !
            </p>
            <button @click="goToEnvironmentGame" class="action-button-gaming">
              Lancer le jeu
            </button>
          </div>

          <!-- Progression -->
          <div
            v-if="getEnvironmentPreferences().totalRoomsEvaluated > 0"
            class="hq-progress-bar"
          >
            <div class="progress-label">Exploration de la carte</div>
            <div class="progress-track">
              <div class="progress-fill-gaming" :style="{ width: getEnvironmentPreferences().completion + '%' }"></div>
            </div>
            <div class="progress-value">{{ getEnvironmentPreferences().completion }}% scanné ({{ getEnvironmentPreferences().totalRoomsEvaluated }}/2 zones)</div>
          </div>
        </div>
      </div>

      <!-- Section Intérêts Professionnels (Ma Collection) -->
      <div v-if="currentTab === 'career'" class="profile-section career-showcase">
        <div class="section-header-gaming career-theme">
          <h2 class="gaming-title">
            <span class="gaming-icon">💼</span>
            Ma Collection de Métiers
          </h2>
        </div>

        <!-- Indicateur de chargement -->
        <div v-if="isLoadingJobs" class="loading-skills-modern">
          <div class="loading-spinner-modern"></div>
          <p>Chargement du catalogue...</p>
        </div>

        <!-- Message d'erreur -->
        <div v-if="jobsError && !isLoadingJobs" class="skills-error-modern">
          <p class="error-message">{{ jobsError }}</p>
          <button @click="refreshJobsData" class="retry-btn">
            Réessayer
          </button>
        </div>

        <div class="career-dashboard" v-if="!isLoadingJobs && !jobsError">
          
          <!-- Career HUD -->
          <div class="career-hud">
            <div class="hud-item">
              <div class="hud-value">{{ jobsStatistics.totalAnswered }}</div>
              <div class="hud-label">Missions Explorées</div>
            </div>
            <div class="hud-item highlight-liked">
              <div class="hud-value">{{ jobsStatistics.liked }}</div>
              <div class="hud-label">Favoris (Coup de Coeur)</div>
            </div>
            <div class="hud-item">
              <div class="hud-value">{{ jobsStatistics.completion }}%</div>
              <div class="hud-label">Exploration Globale</div>
            </div>
          </div>

          <!-- Ma Collection (Liked Jobs as Trading Cards) -->
          <div v-if="likedJobs.length > 0" class="collection-section">
            <h3 class="subsection-title-gaming">🎴 Mes Cartes Favoris</h3>
            <div class="collection-grid">
              <div
                v-for="job in likedJobs"
                :key="job.id"
                class="job-trading-card"
                @click="viewJobDetails(job)"
              >
                <div class="card-rarity-glow"></div>
                <div class="card-inner">
                  <div class="card-visual" :data-job="job.name">
                    <span class="job-emoji-large">💼</span>
                  </div>
                  <div class="card-info">
                    <h4 class="card-title">{{ job.name }}</h4>
                    <div class="card-tags">
                      <span v-for="(skill, sIdx) in job.skills.slice(0, 2)" :key="sIdx" class="card-tag">
                        {{ skill }}
                      </span>
                    </div>
                  </div>
                  <div class="card-footer">
                    <span class="card-action-text">DÉTAILS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-deck">
            <div class="empty-deck-icon">🔍</div>
            <h3>Catalogue Vide</h3>
            <p>Tu n'as pas encore validé de métiers favoris. Explore le jeu des métiers pour remplir ta collection !</p>
            <button @click="goToJobDiscovery" class="action-button-gaming">
              Lancer la mission
            </button>
          </div>

          <!-- Mission Log (All choices) -->
          <div v-if="Object.keys(jobChoicesData.jobChoices || {}).length > 0" class="mission-log-section">
            <h3 class="subsection-title-gaming">📋 Journal de Bord</h3>
            <div class="mission-log-container">
              <div
                v-for="(choice, jobName) in jobChoicesData.jobChoices"
                :key="jobName"
                class="log-entry"
                :class="choice"
              >
                <div class="log-status-icon">
                  <span v-if="choice === 'like'">✅</span>
                  <span v-else-if="choice === 'dislike'">❌</span>
                  <span v-else>❓</span>
                </div>
                <div class="log-content">
                  <span class="log-job-name">{{ jobName }}</span>
                  <span class="log-date">Exploré le {{ formatDate(new Date()) }}</span>
                </div>
                <div class="log-result-tag">
                  {{ choice === 'like' ? 'APPROUVÉ' : choice === 'dislike' ? 'REJETÉ' : 'À REVOIR' }}
                </div>
              </div>
            </div>
          </div>

          <!-- Career Intel (Skills Match) -->
          <div class="career-intel-section">
            <h3 class="subsection-title-gaming">📡 Analyse de Profil</h3>
            <div class="intel-grid">
              <div
                v-for="(skill, index) in topCareerSkills"
                :key="index"
                class="intel-stat-card"
              >
                <div class="intel-stat-icon">
                  {{ getSkillIcon(skill.id) }}
                </div>
                <div class="intel-stat-info">
                  <div class="intel-stat-name">{{ skill.name }}</div>
                  <div class="intel-stat-match">
                    Détecté dans <strong>{{ skill.matchingJobs }}</strong> métiers
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Section Accompagnants (Mon Alliance) -->
      <div v-if="currentTab === 'accompanists'" class="profile-section team-showcase">
        <div class="section-header-gaming team-theme">
          <h2 class="gaming-title">
            <span class="gaming-icon">🤝</span>
            Mon Alliance
          </h2>
          <button @click="loadObservers" class="refresh-skills-btn-modern" :disabled="isLoadingObservers" title="Rechercher des signaux">
            <span class="refresh-icon">🔄</span>
          </button>
        </div>

        <!-- Indicateur de chargement -->
        <div v-if="isLoadingObservers" class="loading-skills-modern">
          <div class="loading-spinner-modern"></div>
          <p>Scan des fréquences alliées...</p>
        </div>

        <!-- Message d'erreur -->
        <div v-if="observersError && !isLoadingObservers" class="skills-error-modern">
          <p class="error-message">{{ observersError }}</p>
          <button @click="loadObservers" class="retry-btn">
            Réessayer
          </button>
        </div>

        <div v-if="!isLoadingObservers && !observersError" class="team-dashboard">
          
          <!-- Invitations (Signal Entrant) -->
          <div v-if="pendingInvitations.length > 0" class="signal-alerts-section">
            <h3 class="subsection-title-gaming alert">📡 Signal Entrant</h3>
            <div class="signals-container">
              <div
                v-for="invitation in pendingInvitations"
                :key="invitation.connection_id"
                class="signal-card"
              >
                <div class="signal-pulse"></div>
                <div class="signal-content">
                  <div class="signal-avatar">
                    {{ invitation.observer.first_name.charAt(0) }}{{ invitation.observer.last_name.charAt(0) }}
                  </div>
                  <div class="signal-info">
                    <div class="signal-name">{{ invitation.observer.first_name }} {{ invitation.observer.last_name }}</div>
                    <div class="signal-meta">Demande de renfort • {{ invitation.observer.role_name }}</div>
                  </div>
                  <div class="signal-actions">
                    <button @click="acceptInvitation(invitation.connection_id)" class="accept-signal-btn">ACCEPTER</button>
                    <button @click="rejectInvitation(invitation.connection_id)" class="reject-signal-btn">IGNORER</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Accompagnants Actifs (Membres de l'Escouade) -->
          <div v-if="activeObservers.length > 0" class="squad-section">
            <h3 class="subsection-title-gaming">👥 Mon Escouade <span class="squad-count">{{ activeObservers.length }}</span></h3>
            <div class="squad-grid">
              <div
                v-for="observer in activeObservers"
                :key="observer.connection_id"
                class="member-dossier"
              >
                <div class="member-header">
                  <div class="member-avatar-glow">
                    <div class="member-avatar">
                      {{ observer.observer.first_name.charAt(0) }}{{ observer.observer.last_name.charAt(0) }}
                    </div>
                  </div>
                  <div class="member-status-tag">OPÉRATIONNEL</div>
                </div>
                <div class="member-body">
                  <div class="member-name">{{ observer.observer.first_name }} {{ observer.observer.last_name }}</div>
                  <div class="member-role-badge">{{ observer.observer.role_name }}</div>
                  <div class="member-stats">
                    <div class="m-stat">
                      <span class="m-label">Liaison</span>
                      <span class="m-value">{{ formatDate(observer.date_connected) }}</span>
                    </div>
                  </div>
                  <div v-if="observer.connection_notes" class="member-intel">
                    <span class="intel-icon">💬</span>
                    <p class="intel-text">{{ observer.connection_notes }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- État vide (Recrutement) -->
          <div
            v-if="activeObservers.length === 0 && pendingInvitations.length === 0"
            class="empty-deck recruitment-theme"
          >
            <div class="empty-deck-icon">🔍</div>
            <h3>Base isolée</h3>
            <p>Ton alliance n'a pas encore de membres. Pour recruter des alliés (profs, parents), partage ton code unique !</p>
            <button @click="currentTab = 'personal'" class="action-button-gaming">
              Voir mon code de recrutement
            </button>
          </div>

        </div>
      </div>
    </div>

    <!-- Modales -->
    <!-- Éditeur de biographie -->
    <div v-if="showBioEditor" class="modal-overlay">
      <div class="modal-content">
        <h3 class="modal-title">Modifier ma description</h3>
        <textarea
          v-model="editedBio"
          class="bio-textarea"
          placeholder="Parle de toi, de ce que tu aimes..."
        ></textarea>
        <div class="modal-actions">
          <button
            @click="showBioEditor = false"
            class="modal-button cancel-button"
          >
            Annuler
          </button>
          <button @click="saveBio" class="modal-button save-button">
            Enregistrer
          </button>
        </div>
      </div>
    </div>

    <!-- Éditeur de loisirs -->
    <div v-if="showHobbyEditor" class="modal-overlay">
      <div class="modal-content">
        <h3 class="modal-title">Modifier mes loisirs</h3>
        <div class="hobby-editor">
          <div class="current-hobbies">
            <div
              v-for="(hobby, index) in editedHobbies"
              :key="index"
              class="hobby-editor-item"
            >
              <span class="hobby-name">{{ hobby }}</span>
              <button @click="removeHobby(index)" class="remove-hobby-button">
                ✕
              </button>
            </div>
          </div>
          <div class="add-hobby-form">
            <input
              v-model="newHobby"
              class="hobby-input"
              placeholder="Ajoute un loisir..."
              @keyup.enter="addHobby"
            />
            <button @click="addHobby" class="add-button">Ajouter</button>
          </div>
        </div>
        <div class="modal-actions">
          <button
            @click="showHobbyEditor = false"
            class="modal-button cancel-button"
          >
            Annuler
          </button>
          <button @click="saveHobbies" class="modal-button save-button">
            Enregistrer
          </button>
        </div>
      </div>
    </div>

    <!-- Détails du badge -->
    <div v-if="selectedBadge" class="modal-overlay" @click="closeModal">
      <div class="badge-modal" @click.stop>
        <button class="close-button" @click="closeModal">×</button>

        <div class="badge-detail-content">
          <div
            class="badge-detail-icon"
            :style="{ backgroundColor: selectedBadge.iconColor || '#e0e0e0' }"
          >
            <span class="badge-detail-emoji">{{ selectedBadge.icon }}</span>
          </div>

          <h2 class="badge-detail-title">{{ selectedBadge.title }}</h2>

          <p class="badge-detail-description">
            {{
              selectedBadge.unlocked
                ? selectedBadge.description
                : selectedBadge.hint ||
                  'Continue à jouer pour débloquer ce badge !'
            }}
          </p>

          <div v-if="selectedBadge.unlocked" class="badge-achievement">
            <div class="achievement-date">
              Obtenu le: {{ formatDate(selectedBadge.dateUnlocked) }}
            </div>
            <div class="achievement-game">Dans: {{ selectedBadge.game }}</div>
          </div>
          <div v-else class="badge-locked-info">
            <div class="badge-hint">
              <span class="hint-icon">💡</span>
              <p>
                {{
                  selectedBadge.hint ||
                  'Continue à jouer pour débloquer ce badge !'
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Guide d'aide contextuel -->
    <!-- <div class="profile-guide" v-if="showGuide">
      <div class="guide-character">
        <img src="@/assets/flamou/hey.png" alt="Guide" class="guide-avatar" />
      </div>
      <div class="guide-bubble">
        <p>{{ guideMessage }}</p>
        <button @click="dismissGuide" class="guide-dismiss-button">Compris !</button>
      </div>
    </div> -->
    <div v-if="selectedJob" class="modal-overlay" @click="closeJobModal">
      <div class="job-modal" @click.stop>
        <button class="close-button" @click="closeJobModal">×</button>

        <div class="job-detail-content">
          <!-- Icône du métier -->
          <div class="job-detail-icon" :data-job="selectedJob.name">
            <!-- L'icône sera générée par CSS ::before -->
          </div>

          <h2 class="job-detail-title">{{ selectedJob.name }}</h2>

          <div class="job-detail-description">
            <h3>Description</h3>
            <p>{{ selectedJob.description }}</p>
          </div>

          <div
            class="job-detail-skills"
            v-if="selectedJob.skills && selectedJob.skills.length > 0"
          >
            <h3>Compétences requises</h3>
            <div class="job-skills-grid">
              <div
                v-for="(skill, index) in selectedJob.skills"
                :key="index"
                class="job-skill-badge"
              >
                <span class="skill-icon">✨</span>
                <span class="skill-text">{{ skill }}</span>
              </div>
            </div>
          </div>

          <div
            class="job-detail-info"
            v-if="selectedJob.sector || selectedJob.salary"
          >
            <h3>Informations supplémentaires</h3>
            <div class="job-info-grid">
              <div v-if="selectedJob.sector" class="job-info-item">
                <span class="info-label">Secteur :</span>
                <span class="info-value">{{ selectedJob.sector }}</span>
              </div>
              <div v-if="selectedJob.salary" class="job-info-item">
                <span class="info-label">Salaire indicatif :</span>
                <span class="info-value">{{ selectedJob.salary }}</span>
              </div>
              <div v-if="selectedJob.formation" class="job-info-item">
                <span class="info-label">Formation :</span>
                <span class="info-value">{{ selectedJob.formation }}</span>
              </div>
            </div>
          </div>

          <div class="job-modal-actions">
            <button
              @click="removeJobFromLiked"
              class="modal-button remove-button"
            >
              <span class="btn-icon">💔</span>
              <span class="btn-text">Ne plus aimer</span>
            </button>
            <button
              @click="closeJobModal"
              class="modal-button close-modal-button"
            >
              <span class="btn-icon">✅</span>
              <span class="btn-text">Fermer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import html2pdf from 'html2pdf.js'
import defaultAvatar from '@/assets/pdp.png'
import AvatarDisplay from '@/components/AvatarDisplay.vue'
// import GuideAvatar from '@/components/GuideComponent.vue'
import AuthService from '@/services/AuthService.js'
import TutorialService from '@/services/TutorialService.js'
import BadgeService from '@/services/BadgeService.js'
import UserJourneyService from '@/services/UserJourneyService.js'
import { metiersData } from '@/data/metiersData.js'

// Pre-require badge images to avoid HMR issues
const BADGE_IMAGES = {
  0: require('@/assets/badges_skills/Badge_explorateur_du_profil.png'),
  1: require('@/assets/badges_skills/Badge_jeu_de_vitesse.png'),
  2: require('@/assets/badges_skills/Badge_jeu_de_scénarios.png'),
  3: require('@/assets/badges_skills/Badge_jeu_de_formes.png'),
  4: require('@/assets/badges_skills/Badge_jeu_des_compétences.png'),
  5: require("@/assets/badges_skills/Badge_jeu_de_l'environnement.png"),
  6: require('@/assets/badges_skills/Badge_CV.png'),
  7: require('@/assets/badges_skills/Badge_jeu_de_metiers.png'),
  8: require('@/assets/badges_skills/Badge_inscription_a_une_formation.png'),
  9: require('@/assets/badges_skills/Badge_tous_les_jeux_finis.png'),
}

export default {
  name: 'UserProfile',
  components: {
    AvatarDisplay,
    // GuideAvatar,
  },
  setup() {
    // Helper functions for badge images
    const getBadgeImagePath = (badgeId) => {
      return BADGE_IMAGES[badgeId] || null
    }

    const shouldUseBadgeImage = (badgeId) => {
      return getBadgeImagePath(badgeId) !== null
    }

    const shouldEnlargeBadgeImage = (badgeId) => {
      return [0, 6, 8, 9].includes(badgeId)
    }

    const isFormationBadge = (badgeId) => {
      return badgeId === 8
    }

    // État du profil
    const isLoadingUserProfile = ref(true)
    const userProfileError = ref(null)
    const userProfile = ref({
      firstName: 'Utilisateur',
      lastName: '',
      age: null,
      city: '',
      email: '',
      username: '',
      avatar: defaultAvatar,
      bio: "Je suis quelqu'un de curieux et j'aime découvrir de nouvelles choses. Je m'intéresse particulièrement aux jeux vidéo et à la musique.",
      hobbies: ['Jeux vidéo', 'Musique', 'Dessin', 'Natation'],
      experience: 150, // Points d'expérience pour calculer le niveau
      youngCode: null, // Code unique du jeune pour le lier à un accompagnant
    })

    // Progression basée sur les badges débloqués (identique au Dashboard)
    const progress = ref(0)

    // États des onglets de navigation
    const tabs = [
      { id: 'personal', name: 'Informations', icon: '👤' },
      { id: 'skills', name: 'Compétences', icon: '🎯' },
      { id: 'environment', name: 'Environnement', icon: '🏢' },
      { id: 'career', name: 'Métiers', icon: '💼' },
      { id: 'accompanists', name: 'Accompagnants', icon: '👥' },
    ]
    const currentTab = ref('personal')

    // États pour les éditeurs et modales
    const showBioEditor = ref(false)
    const editedBio = ref(userProfile.value.bio)
    const showHobbyEditor = ref(false)
    const editedHobbies = ref([...userProfile.value.hobbies])
    const newHobby = ref('')
    const selectedAvatar = ref(userProfile.value.avatar)
    const selectedBadge = ref(null)
    const codeCopied = ref(false)

    // États pour l'animation de badge
    const showBadgeUnlockAnimation = ref(false)
    const newlyUnlockedBadge = ref(null)

    // État d'accessibilité
    const highContrastMode = ref(false)
    const soundEnabled = ref(true)
    const textSizeLevel = ref(0) // 0 = normal, -1 = petit, 1 = grand

    // État du guide contextuel
    const showGuide = ref(true)
    const guideMessage = ref(
      'Bienvenue sur ton profil ! Ici, tu peux voir tes informations, compétences, badges et préférences. Explore les différents onglets pour en savoir plus sur toi !',
    )

    // Variables pour le guide avatar (tutoriel)
    const guideContext = ref('profile')
    const guideOptions = ref([
      { text: 'Faire le tour', action: 'start_tour' },
      { text: 'Plus tard', action: 'dismiss' },
    ])
    const guideForceShow = ref(true)
    const isFirstVisit = ref(true)
    const profileTourCompleted = ref(false)
    const currentTourStep = ref(0)
    const guidePosition = ref({
      top: '20px',
      left: '20px',
    })

    // Variables pour la gestion des compétences
    const isLoadingSkills = ref(false)
    const skillsError = ref(null)

    // Variables pour les données de vitesse
    const isLoadingSpeedData = ref(false)
    const speedDataError = ref(null)
    const speedGameData = ref({})

    // Variables pour les données d'environnement
    const isLoadingEnvironmentData = ref(false)
    const environmentDataError = ref(null)
    const environmentGameData = ref({})

    // Variables réactives pour les métiers (à ajouter dans le setup())
    const isLoadingJobs = ref(false)
    const jobsError = ref(null)
    const jobChoicesData = ref({}) // Données brutes du backend
    const jobCompletionPercentage = ref(0)

    // Variables pour les accompagnants (observateurs)
    const isLoadingObservers = ref(false)
    const observersError = ref(null)
    const observers = ref([]) // Liste de tous les accompagnants
    const pendingInvitations = computed(() =>
      observers.value.filter(obs => !obs.is_active)
    )
    const activeObservers = computed(() =>
      observers.value.filter(obs => obs.is_active)
    )

    // Badges (simulés)
    const badges = ref([
      {
        id: 0,
        title: 'Explorateur du Profil',
        description: 'Tu as découvert ton profil avec succès !',
        icon: '🧭',
        iconColor: '#FF5722',
        unlocked: true,
        game: 'Profil',
        dateUnlocked: '2023-06-15',
      },
      {
        id: 1,
        title: 'Maître de la vitesse',
        description: 'Tu as terminé le jeu de vitesse avec un score parfait !',
        icon: '⚡',
        iconColor: '#F44336',
        unlocked: true,
        dateUnlocked: '2023-06-16',
        game: 'Jeu de Vitesse',
      },
      {
        id: 2,
        title: 'Maître des scénarios',
        description: 'Tu as brillamment résolu ton premier scénario social !',
        icon: '🎭',
        iconColor: '#9C27B0',
        unlocked: false,
        hint: 'Complète tous les scénarios sociaux',
        game: 'Jeu des Scénarios',
      },
      {
        id: 3,
        title: 'Expert des formes',
        description:
          'Tu as reconnu toutes les séquences de formes correctement !',
        icon: '🔷',
        iconColor: '#2196F3',
        unlocked: false,
        hint: 'Termine le jeu des formes avec un score parfait',
        game: 'Jeu des Formes',
      },
      {
        id: 4,
        title: 'Roulette des compétences',
        description: 'Tu as exploré et identifié tes points forts !',
        icon: '🎯',
        iconColor: '#3F51B5',
        unlocked: false,
        hint: 'Termine la Roulette des Compétences',
        game: 'Roulette des Compétences',
      },
    ])

    // Réussites (simulées)
    const achievements = ref([
      {
        title: 'Premier jeu terminé !',
        description: 'Tu as terminé ton premier jeu avec succès.',
        date: '2023-06-15',
        icon: '🎮',
      },
      {
        title: 'Vitesse impressionnante',
        description: 'Tu as atteint 30 mots par minute dans le jeu de vitesse.',
        date: '2023-06-18',
        icon: '⚡',
      },
    ])

    // Métiers aimés (simulés)
    const likedJobs = computed(() => {
      if (!jobChoicesData.value.jobChoices) return []

      // Filtrer les métiers aimés
      const likedJobNames = Object.keys(jobChoicesData.value.jobChoices).filter(
        (jobName) => jobChoicesData.value.jobChoices[jobName] === 'like',
      )

      // Retrouver les objets métiers complets depuis metiersData
      return likedJobNames
        .map((jobName) => {
          return metiersData.find((job) => job.name === jobName)
        })
        .filter((job) => job !== undefined) // Filtrer les métiers non trouvés
    })

    const jobsStatistics = computed(() => {
      if (!jobChoicesData.value.jobChoices) {
        return {
          totalAnswered: 0,
          liked: 0,
          disliked: 0,
          unknown: 0,
          completion: 0,
        }
      }

      const choices = jobChoicesData.value.jobChoices
      const totalAnswered = Object.keys(choices).length
      const liked = Object.values(choices).filter(
        (choice) => choice === 'like',
      ).length
      const disliked = Object.values(choices).filter(
        (choice) => choice === 'dislike',
      ).length
      const unknown = Object.values(choices).filter(
        (choice) => choice === 'unknown',
      ).length

      return {
        totalAnswered,
        liked,
        disliked,
        unknown,
        completion: Math.round((jobCompletionPercentage.value || 0) * 100),
      }
    })

    // Catégories de compétences
    const skillCategories = ref([
      {
        id: 'communication',
        name: 'Communication',
        color: '#FF5722',
        icon: '💬',
      },
      {
        id: 'interpersonnel',
        name: 'Relations Sociales',
        color: '#E91E63',
        icon: '👥',
      },
      { id: 'adaptation', name: 'Adaptation', color: '#9C27B0', icon: '🔄' },
      {
        id: 'professionnelles',
        name: 'Organisationnelles',
        color: '#673AB7',
        icon: '📊',
      },
      {
        id: 'personnelles',
        name: 'Personnelles',
        color: '#3F51B5',
        icon: '🧠',
      },
    ])

    // Compétences (chargées depuis le backend)
    const skills = ref([])

    // Styles de CV
    const cvStyles = ref([
      { id: 'simple', name: 'Simple' },
      { id: 'creative', name: 'Créatif' },
      { id: 'professional', name: 'Professionnel' },
    ])
    const currentCVStyle = ref('simple')

    // Couleurs de CV
    const cvColors = ref([
      { name: 'Bleu', value: '#2196F3' },
      { name: 'Vert', value: '#4CAF50' },
      { name: 'Violet', value: '#9C27B0' },
      { name: 'Orange', value: '#FF9800' },
      { name: 'Rouge', value: '#F44336' },
    ])
    const currentCVColor = ref('#2196F3')

    // Variables pour la modale de détails du métier
    const selectedJob = ref(null)

    // Charger les données de vitesse
    const loadSpeedGameData = async () => {
      try {
        isLoadingSpeedData.value = true
        speedDataError.value = null

        const response = await AuthService.request('get', '/games/speed')
        console.log('Données du jeu de vitesse:', response.data)

        if (response.data && typeof response.data === 'object') {
          speedGameData.value = response.data
        } else {
          speedGameData.value = {}
        }
      } catch (error) {
        console.error(
          'Erreur lors du chargement des données de vitesse:',
          error,
        )
        speedDataError.value =
          error.response?.data?.detail ||
          'Erreur lors du chargement des données de vitesse'

        // Fallback vers localStorage
        const savedSpeedData = localStorage.getItem('speedGameData')
        if (savedSpeedData) {
          try {
            speedGameData.value = JSON.parse(savedSpeedData)
          } catch (parseError) {
            speedGameData.value = {}
          }
        }
      } finally {
        isLoadingSpeedData.value = false
      }
    }

    // Charger les données d'environnement
    const loadEnvironmentGameData = async () => {
      try {
        isLoadingEnvironmentData.value = true
        environmentDataError.value = null

        const response = await AuthService.request('get', '/games/room-env')
        console.log("Données du jeu d'environnement:", response.data)

        if (response.data && typeof response.data === 'object') {
          environmentGameData.value = response.data
        } else {
          environmentGameData.value = {}
        }
      } catch (error) {
        console.error(
          "Erreur lors du chargement des données d'environnement:",
          error,
        )
        environmentDataError.value =
          error.response?.data?.detail ||
          "Erreur lors du chargement des données d'environnement"

        // Fallback vers localStorage
        const savedEnvironmentData = localStorage.getItem('environmentGameData')
        if (savedEnvironmentData) {
          try {
            environmentGameData.value = JSON.parse(savedEnvironmentData)
          } catch (parseError) {
            environmentGameData.value = {}
          }
        }
      } finally {
        isLoadingEnvironmentData.value = false
      }
    }

    // Fonction pour évaluer le niveau de vitesse de frappe
    const getTypingSkillLevel = () => {
      if (!speedGameData.value || !speedGameData.value.levelStats) {
        return { level: 0, description: 'Non évalué', wpm: 0, accuracy: 0 }
      }

      const levelStats = speedGameData.value.levelStats
      const currentLevel = speedGameData.value.currentLevel || 0
      const completion = speedGameData.value.completion || 0

      // Calculer la moyenne des performances
      const statsValues = Object.values(levelStats)
      if (statsValues.length === 0) {
        return { level: 0, description: 'Non évalué', wpm: 0, accuracy: 0 }
      }

      const avgWpm =
        statsValues.reduce((acc, stat) => acc + (stat.wpm || 0), 0) /
        statsValues.length
      const avgAccuracy =
        statsValues.reduce((acc, stat) => acc + (stat.accuracy || 0), 0) /
        statsValues.length
      // const successRate = statsValues.filter(stat => stat.success).length / statsValues.length * 100;

      // Déterminer le niveau basé sur les performances et la progression
      let level = 1
      let description = 'Débutant'

      if (currentLevel >= 8 && avgWpm >= 60 && avgAccuracy >= 95) {
        level = 5
        description = 'Expert en frappe'
      } else if (currentLevel >= 6 && avgWpm >= 45 && avgAccuracy >= 90) {
        level = 4
        description = 'Bon niveau de frappe'
      } else if (currentLevel >= 4 && avgWpm >= 30 && avgAccuracy >= 85) {
        level = 3
        description = 'Niveau moyen'
      } else if (currentLevel >= 2 && avgWpm >= 20 && avgAccuracy >= 75) {
        level = 2
        description = 'En progression'
      }

      return {
        level,
        description,
        wpm: Math.round(avgWpm),
        accuracy: Math.round(avgAccuracy),
        currentLevel,
        completion: Math.round(completion),
      }
    }

    // Fonction pour analyser les préférences d'environnement
    const getEnvironmentPreferences = () => {
      if (!environmentGameData.value || !environmentGameData.value.roomData) {
        return {
          preferredEnvironments: [],
          lightPreference: 'Non défini',
          noisePreference: 'Non défini',
          socialPreference: 'Non défini',
          overallComfort: 'Non évalué',
        }
      }

      const roomData = environmentGameData.value.roomData

      // Analyser les environnements préférés (feedback positif)
      const preferredRooms = roomData.filter((room) =>
        ['very calm', 'focused', 'comfortable'].includes(room.userFeedback),
      )

      // Analyser les préférences de lumière
      const lightPreferences = preferredRooms.map((room) => room.lightIntensity)
      const mostPreferredLight = getMostFrequent(lightPreferences) || 'normal'

      // Analyser les préférences de bruit
      const noisePreferences = preferredRooms.map((room) => room.noiseType)
      const mostPreferredNoise = getMostFrequent(noisePreferences) || 'silence'

      // Analyser les préférences sociales
      const peoplePreferences = preferredRooms.map((room) => room.peopleInRoom)
      const avgPeoplePreference =
        peoplePreferences.length > 0
          ? Math.round(
              peoplePreferences.reduce((a, b) => a + b, 0) /
                peoplePreferences.length,
            )
          : 0

      let socialPreference = 'Seul'
      if (avgPeoplePreference >= 5) socialPreference = 'En grand groupe'
      else if (avgPeoplePreference >= 3) socialPreference = 'En petit groupe'
      else if (avgPeoplePreference >= 1)
        socialPreference = 'Avec quelques personnes'

      // Évaluer le confort général
      const comfortableFeedbacks = roomData.filter((room) =>
        ['very calm', 'focused', 'comfortable'].includes(room.userFeedback),
      ).length
      const totalFeedbacks = roomData.length
      const comfortRate =
        totalFeedbacks > 0 ? (comfortableFeedbacks / totalFeedbacks) * 100 : 0

      let overallComfort = "Difficulté d'adaptation"
      if (comfortRate >= 80) overallComfort = 'Très adaptable'
      else if (comfortRate >= 60) overallComfort = 'Bien adaptable'
      else if (comfortRate >= 40) overallComfort = 'Modérément adaptable'

      // Calculer la progression réelle sur 2 pièces (Focus Room, Open Room)
      const actualCompletion = Math.min(Math.round((roomData.length / 2) * 100), 100)

      return {
        preferredEnvironments: preferredRooms.map((room) => ({
          name: room.room,
          feedback: room.userFeedback,
          commentary: room.commentary,
        })),
        lightPreference: getLightDescription(mostPreferredLight),
        noisePreference: getNoiseDescription(mostPreferredNoise),
        socialPreference,
        overallComfort,
        completion: actualCompletion,
        totalRoomsEvaluated: roomData.length,
      }
    }

    // Fonction utilitaire pour trouver l'élément le plus fréquent
    const getMostFrequent = (arr) => {
      if (arr.length === 0) return null
      return arr
        .sort(
          (a, b) =>
            arr.filter((v) => v === a).length -
            arr.filter((v) => v === b).length,
        )
        .pop()
    }

    // Descriptions pour les préférences de lumière
    const getLightDescription = (lightType) => {
      const descriptions = {
        low: 'Éclairage tamisé',
        normal: 'Éclairage standard',
        high: 'Éclairage vif',
      }
      return descriptions[lightType] || lightType
    }

    // Descriptions pour les préférences de bruit
    const getNoiseDescription = (noiseType) => {
      const descriptions = {
        silence: 'Silence complet',
        'white noise': 'Bruit blanc',
        natural: 'Sons naturels',
        coffee: 'Ambiance café',
        working: 'Bruit de bureau',
      }
      return descriptions[noiseType] || noiseType
    }

    // Fonction pour traduire le feedback utilisateur
    const getFeedbackText = (feedback) => {
      const translations = {
        'very calm': 'Très calme',
        focused: 'Concentré',
        comfortable: "À l'aise",
        neutral: 'Neutre',
        uncomfortable: 'Inconfortable',
        overstimulated: 'Surstimulé',
      }
      return translations[feedback] || feedback
    }

    // Navigation vers le jeu d'environnement
    const goToEnvironmentGame = () => {
      window.location.href = '/jeune/environment'
    }

    // Fonction pour afficher les détails d'un métier
    const viewJobDetails = (job) => {
      selectedJob.value = job
      console.log('Affichage des détails pour le métier:', job)
    }

    // Fonction pour fermer la modale de métier
    const closeJobModal = () => {
      selectedJob.value = null
    }

    // Fonction pour retirer un métier des favoris
    const removeJobFromLiked = async () => {
      if (!selectedJob.value) return

      try {
        // Mettre à jour le backend
        const response = await AuthService.request('get', '/games/jobs')
        let currentData = response.data || { completion: 0, jobChoices: {} }

        // Supprimer le métier des choix (ou le marquer comme "dislike")
        if (
          currentData.jobChoices &&
          currentData.jobChoices[selectedJob.value.name]
        ) {
          currentData.jobChoices[selectedJob.value.name] = 'dislike'

          // Envoyer la mise à jour au backend
          await AuthService.request('post', '/games/jobs', currentData)

          // Recharger les données locales
          await loadJobsData()

          console.log(`Métier ${selectedJob.value.name} retiré des favoris`)
        }
      } catch (error) {
        console.error(
          'Erreur lors de la suppression du métier des favoris:',
          error,
        )

        // Fallback local en cas d'erreur
        const likedIds = JSON.parse(
          localStorage.getItem('liked-metiers') || '[]',
        )
        const updatedIds = likedIds.filter((id) => id !== selectedJob.value.id)
        localStorage.setItem('liked-metiers', JSON.stringify(updatedIds))

        // Recharger les données
        await loadJobsData()
      }

      // Fermer la modale
      closeJobModal()
    }

    // Fonction pour charger les données des métiers depuis le backend
    const loadJobsData = async () => {
      try {
        isLoadingJobs.value = true
        jobsError.value = null

        console.log('Chargement des données métiers depuis le backend...')

        const response = await AuthService.request('get', '/games/jobs')
        console.log('Données métiers reçues:', response.data)

        if (response.data && typeof response.data === 'object') {
          jobChoicesData.value = response.data
          jobCompletionPercentage.value = response.data.completion || 0

          console.log('Métiers aimés trouvés:', likedJobs.value)
          console.log('Statistiques métiers:', jobsStatistics.value)
        } else {
          // Données invalides, utiliser une structure par défaut
          jobChoicesData.value = { completion: 0, jobChoices: {} }
          jobCompletionPercentage.value = 0
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données métiers:', error)
        jobsError.value =
          error.response?.data?.detail ||
          'Erreur lors du chargement des métiers'

        // Fallback vers localStorage en cas d'erreur
        loadJobsFromLocalStorage()
      } finally {
        isLoadingJobs.value = false
      }
    }

    // Fallback vers localStorage
    const loadJobsFromLocalStorage = () => {
      try {
        console.log('Chargement des métiers depuis localStorage (fallback)')

        // Essayer de charger depuis la nouvelle structure (userJobChoices)
        const newStructureData = localStorage.getItem('userJobChoices')
        if (newStructureData) {
          const parsedData = JSON.parse(newStructureData)
          jobChoicesData.value = parsedData
          jobCompletionPercentage.value = parsedData.completion || 0
          console.log(
            'Données métiers chargées depuis localStorage (nouvelle structure):',
            parsedData,
          )
          return
        }

        // Fallback vers l'ancienne structure (liked-metiers)
        const oldStructureData = localStorage.getItem('liked-metiers')
        if (oldStructureData) {
          const likedIds = JSON.parse(oldStructureData)
          const jobChoices = {}

          // Convertir les IDs aimés en structure de choix
          likedIds.forEach((jobId) => {
            const job = metiersData.find((j) => j.id === jobId)
            if (job) {
              jobChoices[job.name] = 'like'
            }
          })

          jobChoicesData.value = {
            completion: likedIds.length / metiersData.length,
            jobChoices: jobChoices,
          }

          console.log(
            "Données métiers converties depuis l'ancienne structure:",
            jobChoicesData.value,
          )
          return
        }

        // Aucune donnée trouvée
        jobChoicesData.value = { completion: 0, jobChoices: {} }
        jobCompletionPercentage.value = 0
      } catch (error) {
        console.error('Erreur lors du chargement des métiers locaux:', error)
        jobChoicesData.value = { completion: 0, jobChoices: {} }
        jobCompletionPercentage.value = 0
      }
    }

    // Fonction pour actualiser les données métiers
    const refreshJobsData = async () => {
      await loadJobsData()
    }

    // Fonction pour obtenir la couleur selon le type de choix
    const getJobChoiceColor = (choice) => {
      const colors = {
        like: '#4CAF50',
        dislike: '#F44336',
        unknown: '#FF9800',
      }
      return colors[choice] || '#9E9E9E'
    }

    // Fonction pour obtenir le texte selon le type de choix
    const getJobChoiceText = (choice) => {
      const texts = {
        like: 'Ça me plaît',
        dislike: 'Pas pour moi',
        unknown: 'À découvrir',
      }
      return texts[choice] || 'Non évalué'
    }

    // Calculer le niveau de l'utilisateur (identique au Dashboard)
    // Nouveau calcul : 1 niveau tous les 16.67% de progression (6 niveaux max pour 100%)
    // Niveau 1: 0-16%
    // Niveau 2: 17-33%
    // Niveau 3: 34-50%
    // Niveau 4: 51-66%
    // Niveau 5: 67-83%
    // Niveau 6: 84-100%
    const calculateLevel = () => {
      return Math.min(Math.floor(progress.value / 16.67) + 1, 6)
    }

    // Obtenir le pourcentage d'expérience pour le niveau suivant (basé sur progress)
    const getExpPercentage = () => {
      const currentLevel = calculateLevel()
      // Calculer le seuil de début du niveau actuel
      const levelStartThreshold = (currentLevel - 1) * 16.67
      // Calculer la progression dans le niveau actuel
      const progressInLevel = progress.value - levelStartThreshold
      // Calculer le pourcentage dans le niveau (0-100%)
      const percentage = Math.min(Math.max((progressInLevel / 16.67) * 100, 0), 100)
      return percentage
    }

    // Obtenir les compétences d'une catégorie spécifique
    const getSkillsByCategory = (categoryId) => {
      return skills.value.filter((skill) => skill.category === categoryId)
    }

    // Obtenir la couleur correspondant au niveau de compétence
    const getLevelColor = (level) => {
      const colors = [
        '#F44336', // niveau 1 - rouge
        '#FF9800', // niveau 2 - orange
        '#FFC107', // niveau 3 - jaune
        '#4CAF50', // niveau 4 - vert
        '#2196F3', // niveau 5 - bleu
      ]
      return colors[Math.min(level, 5) - 1] || colors[0]
    }

    // Obtenir le nom du niveau de compétence
    const getSkillLevelName = (level) => {
      const levels = [
        'Débutant',
        'Apprenti',
        'Intermédiaire',
        'Avancé',
        'Expert',
      ]
      return levels[Math.min(level, 5) - 1] || levels[0]
    }

    const getSimpleSkillLevel = (level) => {
      const levels = [
        { text: 'Je débute', class: 'level-debut' },
        { text: 'Je progresse', class: 'level-progres' },
        { text: 'Je suis fort', class: 'level-fort' },
        { text: 'Je suis très fort', class: 'level-fort' },
        { text: 'Super champion !', class: 'level-super' },
      ]

      // Retourner l'objet pour le niveau (1-5)
      const simpleLevel = levels[Math.min(level, 5) - 1] || levels[0]
      return `<span class="${simpleLevel.class}">${simpleLevel.text}</span>`
    }

    // Obtenir une icône pour une compétence
    const getSkillIcon = (skillId) => {
      const icons = {
        communication: '📣',
        empathie: '❤️',
        adaptabilite: '🦎',
        creativite: '💡',
        organisation: '📋',
        autonomie: '🦅',
        assertivite: '🗣️',
        diplomatie: '🤝',
        ecouteActive: '👂',
        responsabilite: '🛡️',
        confianceEnSoi: '💪',
        espritEquipe: '👥',
        initiative: '🚀',
        reflexionRapide: '⚡',
        gestionStress: '😌',
        anticipation: '🔮',
        observation: '👁️',
        apprentissage: '📚',
        gestionTemps: '⏱️',
        decision: '✅',
        patience: '🧘',
        curiosite: '🔎',
        pragmatisme: '🧰',
        assurance: '🏆',
        soutien: '🤲',
        mediation: '☮️',
        inclusion: '🌈',
        coaching: '📚',
        negociation: '🔍',
        franchise: '💯',
      }
      return icons[skillId] || '✨'
    }

    // Formater une date
    const formatDate = (dateString) => {
      if (!dateString) return ''
      const options = { day: 'numeric', month: 'long', year: 'numeric' }
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', options)
    }

    // Meilleures compétences
    const topSkills = computed(() => {
      return [...skills.value].sort((a, b) => b.level - a.level).slice(0, 5)
    })

    // Compétences professionnelles les plus pertinentes
    const topCareerSkills = computed(() => {
      // Dans une vraie application, ici on ferait un calcul basé sur les métiers aimés
      return [
        { id: 'creativite', name: 'Créativité', matchingJobs: 5 },
        { id: 'communication', name: 'Communication', matchingJobs: 3 },
        { id: 'adaptabilite', name: 'Adaptabilité', matchingJobs: 2 },
      ]
    })

    // Méthode principale pour charger les compétences depuis le backend
    const loadSkills = async () => {
      try {
        console.log('Chargement des compétences depuis le backend...')

        // Charger les données des deux sources en parallèle
        const [scenarioData, skillsWheelData] = await Promise.all([
          loadScenarioSkills(),
          loadSkillsWheelData(),
        ])

        // Combiner et traiter les données
        const combinedSkills = combineSkillsData(scenarioData, skillsWheelData)

        // Mettre à jour l'état local
        skills.value = combinedSkills

        console.log('Compétences chargées avec succès:', skills.value)
      } catch (error) {
        console.error('Erreur lors du chargement des compétences:', error)
        // Fallback vers localStorage en cas d'erreur
        loadSkillsFromLocalStorage()
      }
    }

    // Charger les compétences du jeu des scénarios
    const loadScenarioSkills = async () => {
      try {
        const response = await AuthService.request('get', '/games/scenario')
        console.log('Données des scénarios:', response.data)

        if (response.data && response.data.traits) {
          return response.data.traits
        }
        return {}
      } catch (error) {
        console.warn('Impossible de charger les données des scénarios:', error)
        return {}
      }
    }

    // Charger les données de la roue des compétences
    const loadSkillsWheelData = async () => {
      try {
        const response = await AuthService.request('get', '/games/skills')
        console.log('Données de la roue des compétences:', response.data)

        if (response.data && response.data.skillsAssessment) {
          return response.data.skillsAssessment
        }
        return {}
      } catch (error) {
        console.warn(
          'Impossible de charger les données de la roue des compétences:',
          error,
        )
        return {}
      }
    }

    // Combiner les données des deux sources
    const combineSkillsData = (scenarioTraits, skillsAssessment) => {
      const combinedSkills = []
      const processedSkills = new Set()

      // Traiter les compétences des scénarios (avec scores numériques)
      Object.entries(scenarioTraits).forEach(([skillId, value]) => {
        if (typeof value === 'number' && value > 0) {
          const skillName = getSkillDisplayName(skillId)
          const category = determineSkillCategory(skillId)

          // Convertir le score numérique en niveau (1-5)
          const level = Math.max(1, Math.min(5, Math.ceil(value / 3)))

          combinedSkills.push({
            id: skillId,
            name: skillName,
            level: level,
            category: category,
            source: 'scenario',
          })

          processedSkills.add(skillId)
        }
      })

      // Traiter les compétences de la roue des compétences
      Object.entries(skillsAssessment).forEach(([category, skills]) => {
        Object.keys(skills).forEach((skillName) => {
          // Convertir le nom affiché vers l'ID de compétence
          const skillId = getSkillIdFromDisplayName(skillName)

          // Ne pas dupliquer si déjà traité
          if (!processedSkills.has(skillId)) {
            const skillCategory = determineSkillCategory(skillId)

            // Attribuer un niveau basé sur la catégorie d'auto-évaluation
            let level = 3 // niveau par défaut
            if (category === 'Strong') level = 5
            else if (category === 'WantToLearn') level = 2
            else if (category === 'Weak') level = 1
            else if (category === 'Unknown') level = 2

            combinedSkills.push({
              id: skillId,
              name: skillName,
              level: level,
              category: skillCategory,
              source: 'wheel',
            })

            processedSkills.add(skillId)
          }
        })
      })

      return combinedSkills
    }

    // Déterminer la catégorie d'une compétence basée sur son ID
    const determineSkillCategory = (skillId) => {
      const categoryMap = {
        // Communication
        assertivite: 'communication',
        communication: 'communication',
        diplomatie: 'communication',
        ecouteActive: 'communication',
        negociation: 'communication',
        franchise: 'communication',

        // Relations sociales
        empathie: 'interpersonnel',
        espritEquipe: 'interpersonnel',
        soutien: 'interpersonnel',
        mediation: 'interpersonnel',
        inclusion: 'interpersonnel',
        coaching: 'interpersonnel',

        // Adaptation
        adaptabilite: 'adaptation',
        initiative: 'adaptation',
        reflexionRapide: 'adaptation',
        gestionStress: 'adaptation',
        creativite: 'adaptation',

        // Organisationnelles
        responsabilite: 'professionnelles',
        anticipation: 'professionnelles',
        observation: 'professionnelles',
        apprentissage: 'professionnelles',
        gestionTemps: 'professionnelles',
        decision: 'professionnelles',

        // Personnelles
        confianceEnSoi: 'personnelles',
        autonomie: 'personnelles',
        patience: 'personnelles',
        curiosite: 'personnelles',
        pragmatisme: 'personnelles',
        assurance: 'personnelles',
      }

      return categoryMap[skillId] || 'personnelles'
    }

    // Convertir un nom affiché vers l'ID de compétence
    const getSkillIdFromDisplayName = (displayName) => {
      const nameToIdMap = {
        Assertivité: 'assertivite',
        Communication: 'communication',
        Diplomatie: 'diplomatie',
        'Écoute active': 'ecouteActive',
        Négociation: 'negociation',
        Franchise: 'franchise',
        Empathie: 'empathie',
        "Esprit d'équipe": 'espritEquipe',
        Soutien: 'soutien',
        Médiation: 'mediation',
        Inclusion: 'inclusion',
        Coaching: 'coaching',
        Adaptabilité: 'adaptabilite',
        Initiative: 'initiative',
        'Réflexion rapide': 'reflexionRapide',
        'Gestion du stress': 'gestionStress',
        Créativité: 'creativite',
        Responsabilité: 'responsabilite',
        Anticipation: 'anticipation',
        Observation: 'observation',
        Apprentissage: 'apprentissage',
        'Gestion du temps': 'gestionTemps',
        'Prise de décision': 'decision',
        'Confiance en soi': 'confianceEnSoi',
        Autonomie: 'autonomie',
        Patience: 'patience',
        Curiosité: 'curiosite',
        Pragmatisme: 'pragmatisme',
        Assurance: 'assurance',
      }

      return (
        nameToIdMap[displayName] ||
        displayName.toLowerCase().replace(/[^a-z]/g, '')
      )
    }

    // Fallback vers localStorage (méthode existante modifiée)
    const loadSkillsFromLocalStorage = () => {
      console.log('Chargement des compétences depuis localStorage (fallback)')

      const savedSkills = localStorage.getItem('userSoftSkills')
      if (savedSkills) {
        try {
          const parsedSkills = JSON.parse(savedSkills)
          const mappedSkills = []

          for (const [skillId, value] of Object.entries(parsedSkills)) {
            if (typeof value === 'number' && value > 0) {
              const category = determineSkillCategory(skillId)
              const skillName = getSkillDisplayName(skillId)
              const level = Math.max(1, Math.min(5, Math.ceil(value / 3)))

              mappedSkills.push({
                id: skillId,
                name: skillName,
                level: level,
                category: category,
                source: 'local',
              })
            }
          }

          skills.value = mappedSkills
          console.log('Compétences chargées depuis localStorage:', skills.value)
        } catch (error) {
          console.error(
            'Erreur lors du parsing des compétences locales:',
            error,
          )
          skills.value = []
        }
      } else {
        skills.value = []
      }
    }

    // Actualiser les compétences (bouton de refresh)
    const refreshSkills = async () => {
      isLoadingSkills.value = true
      skillsError.value = null

      try {
        await loadSkills()
        console.log('Compétences actualisées avec succès')
      } catch (error) {
        console.error("Erreur lors de l'actualisation des compétences:", error)
        skillsError.value =
          'Impossible de charger les compétences. Vérifiez votre connexion.'
      } finally {
        isLoadingSkills.value = false
      }
    }

    // --- Méthodes pour les modales ---
    // Sauvegarder la bio
    const saveBio = () => {
      userProfile.value.bio = editedBio.value
      showBioEditor.value = false
      saveUserProfile()
    }

    // Ajouter un hobby
    const addHobby = () => {
      if (newHobby.value.trim()) {
        editedHobbies.value.push(newHobby.value.trim())
        newHobby.value = ''
      }
    }

    // Supprimer un hobby
    const removeHobby = (index) => {
      editedHobbies.value.splice(index, 1)
    }

    // Sauvegarder les hobbies
    const saveHobbies = () => {
      userProfile.value.hobbies = [...editedHobbies.value]
      showHobbyEditor.value = false
      saveUserProfile()
    }

    // Sélectionner un style de CV
    const selectCVStyle = (styleId) => {
      currentCVStyle.value = styleId
    }

    // Sélectionner une couleur de CV
    const selectCVColor = (colorValue) => {
      currentCVColor.value = colorValue
    }

    // --- Méthodes pour les badges ---
    // Afficher les détails d'un badge
    const showBadgeDetails = (badge) => {
      selectedBadge.value = badge
    }

    // Fermer la modale de badge
    const closeModal = () => {
      selectedBadge.value = null
    }

    // --- Méthodes pour le CV ---
    // Imprimer le CV
    const printCV = () => {
      // Créer une feuille de style temporaire pour l'impression
      const style = document.createElement('style')
      style.id = 'print-style'

      style.innerHTML = `
          @media print {
          @page {
              margin: 0;
              size: A4;
          }
          body {
              margin: 1cm;
          }
          /* Cacher les en-têtes et pieds de page du navigateur */
          html {
              height: 100%;
          }
          /* Cacher les éléments qui ne font pas partie du CV */
          .profile-header,
          .navigation-tabs,
          .accessibility-controls,
          .cv-customize,
          .cv-actions,
          .profile-guide {
              display: none !important;
          }
          }
      `

      document.head.appendChild(style)
      window.print()

      // Nettoyer après l'impression
      setTimeout(() => {
        const printStyle = document.getElementById('print-style')
        if (printStyle) printStyle.remove()
      }, 1000)
    }

    // Télécharger le CV
    const downloadCV = () => {
      // Vérifier si html2pdf est disponible
      if (typeof html2pdf === 'undefined') {
        alert(
          "La bibliothèque de génération PDF n'est pas disponible. Veuillez rafraîchir la page.",
        )
        return
      }

      const cvElement = document.querySelector('.cv-preview')
      if (!cvElement) {
        alert('Impossible de générer le CV. Veuillez réessayer.')
        return
      }

      // Clone l'élément pour ne pas modifier l'original
      const cvClone = cvElement.cloneNode(true)

      // Supprimer les boutons d'action du clone
      const actionsElement = cvClone.querySelector('.cv-actions')
      if (actionsElement) actionsElement.remove()

      // Options pour la génération du PDF
      const options = {
        margin: [10, 10, 10, 10],
        filename: `cv_${userProfile.value.firstName}_${userProfile.value.lastName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }

      // Afficher un message de chargement
      const loadingMessage = document.createElement('div')
      loadingMessage.style.position = 'fixed'
      loadingMessage.style.top = '50%'
      loadingMessage.style.left = '50%'
      loadingMessage.style.transform = 'translate(-50%, -50%)'
      loadingMessage.style.padding = '20px'
      loadingMessage.style.background = '#3f51b5'
      loadingMessage.style.color = 'white'
      loadingMessage.style.borderRadius = '10px'
      loadingMessage.style.zIndex = '9999'
      loadingMessage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)'
      loadingMessage.innerText = 'Génération du CV en cours...'
      document.body.appendChild(loadingMessage)

      // Générer le PDF
      html2pdf()
        .set(options)
        .from(cvClone)
        .save()
        .then(() => {
          document.body.removeChild(loadingMessage)
        })
        .catch((error) => {
          console.error('Erreur lors de la génération du PDF:', error)
          alert('Une erreur est survenue lors de la génération du PDF.')
          document.body.removeChild(loadingMessage)
        })
    }

    // --- Méthodes de navigation ---
    // Aller à la page de découverte des métiers
    const goToJobDiscovery = () => {
      // Redirection vers la page de découverte des métiers
      window.location.href = '/jeune/metiers'
    }

    // --- Méthodes d'accessibilité ---
    // Activer/désactiver le son
    const toggleSound = () => {
      soundEnabled.value = !soundEnabled.value
      saveAccessibilitySettings()
    }

    // Activer/désactiver le mode contraste élevé
    const toggleHighContrast = () => {
      highContrastMode.value = !highContrastMode.value
      saveAccessibilitySettings()
    }

    // Augmenter la taille du texte
    const increaseTextSize = () => {
      if (textSizeLevel.value < 1) {
        textSizeLevel.value++
        applyTextSize()
        saveAccessibilitySettings()
      }
    }

    // Diminuer la taille du texte
    const decreaseTextSize = () => {
      if (textSizeLevel.value > -1) {
        textSizeLevel.value--
        applyTextSize()
        saveAccessibilitySettings()
      }
    }

    // Appliquer la taille de texte
    const applyTextSize = () => {
      const container = document.querySelector('.profile-container')
      if (!container) return

      container.classList.remove('text-small', 'text-large')

      if (textSizeLevel.value === -1) {
        container.classList.add('text-small')
      } else if (textSizeLevel.value === 1) {
        container.classList.add('text-large')
      }
    }

    // --- Méthodes de stockage ---
    // Sauvegarder le profil utilisateur
    const saveUserProfile = () => {
      localStorage.setItem('userProfile', JSON.stringify(userProfile.value))
    }

    // Sauvegarder les paramètres d'accessibilité
    const saveAccessibilitySettings = () => {
      const settings = {
        highContrastMode: highContrastMode.value,
        soundEnabled: soundEnabled.value,
        textSizeLevel: textSizeLevel.value,
      }
      localStorage.setItem('accessibilitySettings', JSON.stringify(settings))
    }

    // Charger les paramètres d'accessibilité
    const loadAccessibilitySettings = () => {
      const savedSettings = localStorage.getItem('accessibilitySettings')
      if (savedSettings) {
        const settings = JSON.parse(savedSettings)
        highContrastMode.value = settings.highContrastMode || false
        soundEnabled.value =
          settings.soundEnabled !== undefined ? settings.soundEnabled : true
        textSizeLevel.value = settings.textSizeLevel || 0
        applyTextSize()
      }
    }

    // --- Méthodes relatives au guide ---
    // Fermer le guide
    const dismissGuide = () => {
      showGuide.value = false
      localStorage.setItem('profileGuideShown', 'true')
    }

    // Animation de fermeture de badge
    const closeBadgeAnimation = () => {
      showBadgeUnlockAnimation.value = false
    }

    // --- Méthodes pour le guide avatar (tutoriel) ---

    // Gérer les options sélectionnées dans le guide
    const handleGuideOptionSelected = (option) => {
      console.log('Option guide sélectionnée:', option)

      if (option.action === 'start_tour') {
        startProfileTour()
      } else if (option.action === 'dismiss') {
        guideForceShow.value = false
        isFirstVisit.value = false
        saveProfileTutorialProgress(profileTourCompleted.value)
      } else if (option.action === 'next_step') {
        nextTourStep()
      } else if (option.action === 'finish_tour') {
        finishProfileTour()
      }
    }

    // Commencer le tour du profil
    const startProfileTour = () => {
      currentTourStep.value = 1
      guideForceShow.value = true

      // Étape 1: Présentation des onglets
      guideMessage.value =
        'Commençons par explorer les différents onglets ! Ici tu peux voir tes informations personnelles, tes compétences, ton environnement préféré et tes métiers favoris.'
      guideOptions.value = [{ text: 'Suivant', action: 'next_step' }]

      // Positionner le guide près des onglets
      guidePosition.value = {
        top: '140px',
        left: '20px',
      }
    }

    // Passer à l'étape suivante du tour
    const nextTourStep = () => {
      currentTourStep.value++

      switch (currentTourStep.value) {
        case 2:
          // Étape 2: Parler de l'avatar et du niveau
          guideMessage.value =
            "Vois-tu ton avatar et ton niveau ? Plus tu joues aux jeux, plus tu gagnes d'expérience et débloque de nouveaux badges !"
          guideOptions.value = [{ text: 'Suivant', action: 'next_step' }]
          guidePosition.value = {
            top: '200px',
            left: '20px',
          }
          break

        case 3:
          // Étape 3: Parler des compétences
          currentTab.value = 'skills'
          guideMessage.value =
            "Dans l'onglet Compétences, tu peux voir tes talents développés en jouant aux jeux. Chaque jeu t'aide à découvrir tes forces !"
          guideOptions.value = [{ text: 'Suivant', action: 'next_step' }]
          break

        case 4:
          // Étape 4: Parler de l'environnement
          currentTab.value = 'environment'
          guideMessage.value =
            "L'onglet Environnement te montre dans quels types de lieux tu te sens le mieux pour travailler. As-tu déjà joué au jeu des environnements ?"
          guideOptions.value = [{ text: 'Suivant', action: 'next_step' }]
          break

        case 5:
          // Étape 5: Parler des métiers
          currentTab.value = 'career'
          guideMessage.value =
            "Dans Métiers, tu retrouves tous les jobs qui t'intéressent ! Plus tu joues au jeu des métiers, plus tu découvres ce qui te plaît."
          guideOptions.value = [{ text: 'Suivant', action: 'next_step' }]
          break

        case 6:
          // Étape 6: Parler des accompagnants
          currentTab.value = 'accompanists'
          guideMessage.value =
            "Dans Accompagnants, tu peux voir qui te suit dans ton aventure ! Partage ton code de liaison avec tes enseignants ou parents pour qu'ils puissent voir tes progrès."
          guideOptions.value = [{ text: 'Terminer', action: 'finish_tour' }]
          break
      }
    }

    // Terminer le tour du profil
    // Initialiser la progression du tutoriel depuis le backend
    const initializeProfileTutorialProgress = async () => {
      try {
        const progress = await TutorialService.getProgress()

        // Charger completed et visited depuis userProfileCompleted/hasVisitedUserProfile
        profileTourCompleted.value = progress.userProfileCompleted || false
        isFirstVisit.value = !progress.hasVisitedUserProfile

        // Charger currentStep depuis profileTour si disponible
        if (progress.profileTour) {
          currentTourStep.value = progress.profileTour.currentStep || 0
        } else {
          currentTourStep.value = 0
        }

        console.log('Progression du tutoriel ProfilePage chargée depuis le backend:', progress)
      } catch (error) {
        console.error('Erreur lors du chargement de la progression ProfilePage:', error)
        // En cas d'erreur, utiliser localStorage en fallback
        profileTourCompleted.value = localStorage.getItem('profileTourCompleted') === 'true'
        isFirstVisit.value = localStorage.getItem('profileFirstVisit') !== 'false'
      }
    }

    // Sauvegarder la progression du tutoriel dans le backend
    const saveProfileTutorialProgress = async (completed = false) => {
      try {
        if (completed) {
          profileTourCompleted.value = true
          isFirstVisit.value = false
        }

        await TutorialService.updateProfileProgress({
          completed: completed,
          visited: !isFirstVisit.value,
          active: false,
          currentStep: currentTourStep.value,
        })
        console.log('Progression UserProfile sauvegardée dans le backend')
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la progression UserProfile:', error)
        // Fallback sur localStorage en cas d'erreur
        if (completed) {
          localStorage.setItem('profileTourCompleted', 'true')
          localStorage.setItem('profileFirstVisit', 'false')
        }
      }
    }

    // Vérifier et débloquer le badge de profil
    const checkProfileBadge = async () => {
      // Trouver le badge "Explorateur du Profil" (ID 0)
      const profileBadge = badges.value.find((badge) => badge.id === 0)

      // Vérifier si la visite du profil est terminée
      if (profileBadge && !profileBadge.unlocked && profileTourCompleted.value) {
        try {
          // Débloquer le badge via l'API
          const unlockedBadge = await BadgeService.unlockBadge(0)

          // Mettre à jour le badge local
          profileBadge.unlocked = true
          profileBadge.dateUnlocked =
            unlockedBadge.date_unlocked || new Date().toISOString().split('T')[0]

          // Déclencher l'animation
          newlyUnlockedBadge.value = profileBadge

          // Attendre un peu avant d'afficher l'animation
          setTimeout(() => {
            showBadgeUnlockAnimation.value = true
          }, 500)
        } catch (error) {
          console.error('Erreur lors du déblocage du badge de profil:', error)

          // Fallback: déblocage local uniquement
          profileBadge.unlocked = true
          profileBadge.dateUnlocked = new Date().toISOString().split('T')[0]
        }
      }
    }

    const finishProfileTour = async () => {
      currentTab.value = 'personal' // Retourner à l'onglet principal

      // Sauvegarder dans le backend via UserJourneyService
      if (typeof UserJourneyService !== 'undefined') {
        UserJourneyService.completeProfileTour()
      }

      // Mettre à jour l'état local
      profileTourCompleted.value = true
      isFirstVisit.value = false

      // Vérifier et débloquer le badge
      await checkProfileBadge()

      guideForceShow.value = true

      // Message de fin
      guideMessage.value =
        'Tu connais maintenant toutes les sections de ton profil ! Tu peux explorer tes badges et commencer à jouer pour en débloquer de nouveaux.'
      guideOptions.value = [{ text: 'Commencer à jouer', action: 'dismiss' }]
    }

    // Préparer le guide pour la redécouverte
    const prepareGuideForRediscovery = () => {
      if (profileTourCompleted.value) {
        guideMessage.value =
          "Salut ! As-tu des questions sur ton profil ? Je peux t'aider à comprendre tes compétences, badges ou préférences !"
        guideOptions.value = [
          { text: 'Refaire le tour', action: 'start_tour' },
          { text: 'Plus tard', action: 'dismiss' },
        ]
        guideForceShow.value = false
      }
    }

    // Réinitialiser l'état du guide
    const resetGuideState = () => {
      guideForceShow.value = false
      if (!profileTourCompleted.value && isFirstVisit.value) {
        // Si c'est toujours la première visite, remettre le message initial
        setTimeout(() => {
          guideMessage.value =
            'Bienvenue sur ton profil ! Veux-tu que je te fasse visiter ?'
          guideOptions.value = [
            { text: 'Faire le tour', action: 'start_tour' },
            { text: 'Plus tard', action: 'dismiss' },
          ]
        }, 1000)
      }
    }

    // Redémarrer le tour du profil
    const restartProfileTour = () => {
      profileTourCompleted.value = false
      currentTourStep.value = 0
      isFirstVisit.value = true
      guideForceShow.value = true

      guideMessage.value =
        'Bienvenue sur ton profil ! Veux-tu que je te fasse visiter ?'
      guideOptions.value = [
        { text: 'Faire le tour', action: 'start_tour' },
        { text: 'Plus tard', action: 'dismiss' },
      ]

      guidePosition.value = {
        top: '20px',
        left: '20px',
      }
    }

    // --- Méthodes de récupération des données utilisateur ---
    const fetchUserProfile = async () => {
      try {
        isLoadingUserProfile.value = true
        userProfileError.value = null

        // Appel API pour récupérer le profil utilisateur
        const response = await AuthService.request('get', '/user/profile')

        // Charger les passions depuis les données d'avatar de l'onboarding
        let hobbiesFromOnboarding = []
        try {
          const avatarCreationResponse = await AuthService.request(
            'get',
            '/user/profile/avatar-creation-data',
          )
          if (
            avatarCreationResponse.data &&
            avatarCreationResponse.data.avatarPassions
          ) {
            hobbiesFromOnboarding = avatarCreationResponse.data.avatarPassions
            console.log(
              "Passions récupérées depuis l'onboarding:",
              hobbiesFromOnboarding,
            )
          }
        } catch (avatarError) {
          console.log(
            "Aucune donnée d'avatar trouvée, utilisation des hobbies par défaut",
          )
        }

        // Mise à jour des données utilisateur
        userProfile.value = {
          ...userProfile.value,
          firstName: response.data.first_name || 'Utilisateur',
          lastName: response.data.last_name || '',
          age: response.data.age || null,
          city: response.data.city || response.data.address || '',
          email: response.data.email || '',
          username: response.data.username || '',
          avatar: response.data.avatar || defaultAvatar,
          // Utiliser les passions de l'onboarding comme hobbies si disponibles
          hobbies:
            hobbiesFromOnboarding.length > 0
              ? hobbiesFromOnboarding
              : userProfile.value.hobbies,
        }

        console.log('Profil utilisateur chargé:', userProfile.value)

        // Sauvegarder dans le cache
        saveUserProfileCache()
      } catch (error) {
        console.error('Erreur lors du chargement du profil utilisateur:', error)
        userProfileError.value =
          error.response?.data?.detail || 'Erreur lors du chargement du profil'

        // Garder les valeurs par défaut en cas d'erreur
        // mais essayer de charger depuis le cache
        loadCachedUserProfile()
      } finally {
        isLoadingUserProfile.value = false
      }
    }

    const loadCachedUserProfile = () => {
      try {
        const cachedProfile = localStorage.getItem('userProfile')
        if (cachedProfile) {
          const parsedProfile = JSON.parse(cachedProfile)

          // Essayer de récupérer aussi les passions depuis user_profile (onboarding)
          let hobbiesFromOnboarding = parsedProfile.hobbies || []
          try {
            const onboardingProfile = localStorage.getItem('user_profile')
            if (onboardingProfile) {
              const onboardingData = JSON.parse(onboardingProfile)
              if (
                onboardingData.avatarPassions &&
                onboardingData.avatarPassions.length > 0
              ) {
                hobbiesFromOnboarding = onboardingData.avatarPassions
                console.log(
                  'Passions récupérées depuis le cache onboarding:',
                  hobbiesFromOnboarding,
                )
              }
            }
          } catch (onboardingError) {
            console.log("Aucune donnée d'onboarding en cache")
          }

          userProfile.value = {
            ...userProfile.value,
            ...parsedProfile,
            avatar: parsedProfile.avatar || defaultAvatar,
            hobbies: hobbiesFromOnboarding,
          }
          return true
        }
      } catch (error) {
        console.warn('Erreur lors du chargement du cache utilisateur:', error)
      }
      return false
    }

    const saveUserProfileCache = () => {
      try {
        localStorage.setItem('userProfile', JSON.stringify(userProfile.value))
      } catch (error) {
        console.warn(
          'Erreur lors de la sauvegarde du cache utilisateur:',
          error,
        )
      }
    }

    const refreshUserProfile = async () => {
      await fetchUserProfile()
    }

    // Copier le code dans le presse-papiers
    const copyCodeToClipboard = async () => {
      if (!userProfile.value.youngCode) return

      try {
        await navigator.clipboard.writeText(userProfile.value.youngCode)
        codeCopied.value = true

        // Réinitialiser après 2 secondes
        setTimeout(() => {
          codeCopied.value = false
        }, 2000)
      } catch (error) {
        console.error('Erreur lors de la copie du code:', error)
        // Fallback pour les navigateurs qui ne supportent pas l'API clipboard
        const textArea = document.createElement('textarea')
        textArea.value = userProfile.value.youngCode
        textArea.style.position = 'fixed'
        textArea.style.left = '-9999px'
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          codeCopied.value = true
          setTimeout(() => {
            codeCopied.value = false
          }, 2000)
        } catch (fallbackError) {
          console.error('Erreur lors de la copie (fallback):', fallbackError)
        }
        document.body.removeChild(textArea)
      }
    }

    // Récupérer le code unique du jeune
    const fetchYoungCode = async () => {
      try {
        console.log('Récupération du code unique du jeune...')
        const response = await AuthService.request('get', '/connections/my-attachment-code')

        if (response.data && response.data.attachment_code) {
          userProfile.value.youngCode = response.data.attachment_code
          console.log('Code du jeune récupéré:', userProfile.value.youngCode)

          // Sauvegarder dans le cache
          saveUserProfileCache()
        }
      } catch (error) {
        // Si aucun code n'est trouvé, c'est normal (l'utilisateur n'en a peut-être pas encore)
        if (error.response?.status === 404 || error.response?.data?.detail?.includes('No attachment code found')) {
          console.log('Aucun code d\'attachement trouvé pour cet utilisateur (normal si pas encore généré)')
        } else {
          console.error('Erreur lors de la récupération du code du jeune:', error)
        }
        // En cas d'erreur, on ne fait rien (le code reste null et la section ne s'affiche pas)
      }
    }

    // Charger les observeurs (accompagnants) du jeune
    const loadObservers = async () => {
      try {
        isLoadingObservers.value = true
        observersError.value = null
        console.log('Chargement des accompagnants...')

        // 1. Fetch active connections
        const activeResponse = await AuthService.request('get', '/connections/my-observers')
        let activeList = []
        if (activeResponse.data && Array.isArray(activeResponse.data)) {
           activeList = activeResponse.data.map(obs => ({ ...obs, is_active: true }))
        }

        // 2. Fetch pending requests
        const pendingResponse = await AuthService.request('get', '/connection-requests/me')
        let pendingList = []
        if (pendingResponse.data && pendingResponse.data.has_pending_requests && Array.isArray(pendingResponse.data.pending_requests)) {
           pendingList = pendingResponse.data.pending_requests.map(req => ({
             connection_id: req.request_id,
             observer: req.requester,
             date_connected: req.date_requested,
             connection_notes: req.connection_notes,
             is_active: false
           }))
        }

        // 3. Combine
        observers.value = [...activeList, ...pendingList]
        console.log('Tous les accompagnants (actifs + en attente):', observers.value)

      } catch (error) {
        console.error('Erreur lors du chargement des accompagnants:', error)
        observersError.value =
          error.response?.data?.detail || 'Erreur lors du chargement des accompagnants'
        observers.value = []
      } finally {
        isLoadingObservers.value = false
      }
    }

    // Accepter une invitation d'accompagnant
    const acceptInvitation = async (connectionId) => {
      try {
        console.log('Acception de l\'invitation:', connectionId)
        await AuthService.request('post', `/connection-requests/respond/${connectionId}`, { accept: true })
        // Reload observers to update status
        await loadObservers()
      } catch (error) {
        console.error('Erreur lors de l\'acceptation:', error)
        // Optionally show error to user
      }
    }

    // Refuser une invitation d'accompagnant
    const rejectInvitation = async (connectionId) => {
      try {
        console.log('Refus de l\'invitation:', connectionId)
        await AuthService.request('post', `/connection-requests/respond/${connectionId}`, { accept: false })
        // Reload observers to remove from list
        await loadObservers()
      } catch (error) {
        console.error('Erreur lors du refus:', error)
      }
    }

    const checkAuthenticationStatus = () => {
      if (!AuthService.isAuthenticated()) {
        console.warn('Utilisateur non authentifié')
        return false
      }
      return true
    }

    // Obtenir le nom d'affichage d'une compétence
    const getSkillDisplayName = (skillId) => {
      const displayNames = {
        assertivite: 'Assertivité',
        communication: 'Communication',
        diplomatie: 'Diplomatie',
        ecouteActive: 'Écoute active',
        negociation: 'Négociation',
        franchise: 'Franchise',
        empathie: 'Empathie',
        espritEquipe: "Esprit d'équipe",
        soutien: 'Soutien',
        mediation: 'Médiation',
        inclusion: 'Inclusion',
        coaching: 'Coaching',
        adaptabilite: 'Adaptabilité',
        initiative: 'Initiative',
        reflexionRapide: 'Réflexion rapide',
        gestionStress: 'Gestion du stress',
        creativite: 'Créativité',
        responsabilite: 'Responsabilité',
        anticipation: 'Anticipation',
        observation: 'Observation',
        apprentissage: 'Apprentissage',
        gestionTemps: 'Gestion du temps',
        decision: 'Prise de décision',
        confianceEnSoi: 'Confiance en soi',
        autonomie: 'Autonomie',
        patience: 'Patience',
        curiosite: 'Curiosité',
        pragmatisme: 'Pragmatisme',
        assurance: 'Assurance',
      }

      return displayNames[skillId] || skillId
    }

    // Calculer la progression basée sur les badges débloqués (identique au Dashboard)
    const calculateProgress = async () => {
      try {
        // Charger les badges depuis le backend (même logique que Dashboard)
        const { badges: userBadges } = await BadgeService.getUserBadges()

        // Compter les badges débloqués
        const unlockedBadgesCount = userBadges.filter(badge => badge.unlocked).length
        const totalBadgesCount = userBadges.length // 10 badges au total

        // Calculer la progression : (badges débloqués / total badges) * 100
        const finalProgress = (unlockedBadgesCount / totalBadgesCount) * 100
        progress.value = Math.round(finalProgress)

        // Mettre à jour aussi les badges locaux
        badges.value = userBadges

        console.log(`Progression Profile: ${progress.value}% (${unlockedBadgesCount}/${totalBadgesCount} badges débloqués)`)

        return progress.value
      } catch (error) {
        console.error('Erreur lors du calcul de la progression:', error)

        // Fallback sur localStorage en cas d'erreur
        try {
          const savedBadges = localStorage.getItem('userBadges')
          if (savedBadges) {
            const parsedBadges = JSON.parse(savedBadges)
            const unlockedCount = parsedBadges.filter(b => b.unlocked).length
            progress.value = Math.round((unlockedCount / 10) * 100)
          }
        } catch (fallbackError) {
          console.error('Erreur lors du fallback localStorage:', fallbackError)
        }

        return progress.value
      }
    }

    // Vérifier s'il y a un badge nouvellement débloqué
    const checkNewlyUnlockedBadge = () => {
      const newBadgeInfo = localStorage.getItem('newUnlockedBadge')
      if (newBadgeInfo) {
        try {
          // Récupérer et afficher l'animation du badge
          newlyUnlockedBadge.value = JSON.parse(newBadgeInfo)
          showBadgeUnlockAnimation.value = true

          // Supprimer l'information pour ne pas la réafficher
          localStorage.removeItem('newUnlockedBadge')
        } catch (error) {
          console.error(
            'Erreur lors de la vérification du nouveau badge:',
            error,
          )
        }
      }
    }

    // --- Hooks du cycle de vie ---
    onMounted(async () => {
      // Vérifier l'authentification
      if (!checkAuthenticationStatus()) {
        return
      }

      // Charger le profil depuis le cache en premier (pour un affichage rapide)
      const hasCachedProfile = loadCachedUserProfile()

      if (hasCachedProfile) {
        isLoadingUserProfile.value = false
      }

      // Récupération du profil utilisateur depuis l'API
      await fetchUserProfile()

      // Récupérer le code unique du jeune
      await fetchYoungCode()

      // Charger les accompagnants du jeune
      await loadObservers()

      // Charger les paramètres d'accessibilité
      loadAccessibilitySettings()

      // Vérifier si le guide a déjà été affiché
      const guideShown = localStorage.getItem('profileGuideShown')
      if (guideShown === 'true') {
        showGuide.value = false
      }

      // Charger les données de vitesse et d'environnement
      await loadSpeedGameData()
      await loadEnvironmentGameData()

      // Charger les données des métiers
      await loadJobsData()

      // Charger les badges et calculer la progression (identique au Dashboard)
      await calculateProgress()

      // Charger l'état du tour depuis le backend
      await initializeProfileTutorialProgress()

      // Si c'est la première visite et que le tour n'est pas terminé, on le marque comme terminé et on débloque le badge
      if (isFirstVisit.value && !profileTourCompleted.value) {
        // Marquer comme terminé directement
        profileTourCompleted.value = true
        isFirstVisit.value = false

        if (typeof UserJourneyService !== 'undefined') {
          UserJourneyService.completeProfileTour()
        }

        // Vérifier et débloquer le badge
        await checkProfileBadge()
      }

      /*
      // Si c'est la première visite et que le tour n'est pas terminé
      if (isFirstVisit.value && !profileTourCompleted.value) {
        guideMessage.value =
          'Bienvenue sur ton profil ! Veux-tu que je te fasse visiter ?'
        guideOptions.value = [
          { text: 'Faire le tour', action: 'start_tour' },
          { text: 'Plus tard', action: 'dismiss' },
        ]
      }
      */ else {
        guideForceShow.value = false
      }

      // Initialiser le graphique des compétences après le chargement du DOM
      nextTick(() => {
        if (currentTab.value === 'skills') {
          // initSkillsChart();
        }
      })

      // Charger les compétences depuis le backend
      await loadSkills()

      // Vérifier s'il y a un badge nouvellement débloqué
      checkNewlyUnlockedBadge()
    })

    return {
      // Données d'état
      userProfile,
      progress,
      isLoadingUserProfile,
      userProfileError,
      defaultAvatar,
      currentTab,
      tabs,
      badges,
      achievements,
      skillCategories,
      skills,
      showBioEditor,
      editedBio,
      showHobbyEditor,
      editedHobbies,
      newHobby,
      selectedAvatar,
      highContrastMode,
      soundEnabled,
      textSizeLevel,
      cvStyles,
      currentCVStyle,
      cvColors,
      currentCVColor,
      selectedBadge,
      showBadgeUnlockAnimation,
      newlyUnlockedBadge,
      showGuide,
      guideMessage,
      codeCopied,

      // Variables pour le guide avatar (tutoriel)
      guideContext,
      guideOptions,
      guideForceShow,
      isFirstVisit,
      profileTourCompleted,
      currentTourStep,
      guidePosition,
      topSkills,
      topCareerSkills,

      // Variables pour la gestion des compétences
      isLoadingSkills,
      skillsError,

      // Variables pour les données de vitesse
      isLoadingSpeedData,
      speedDataError,
      speedGameData,

      // Variables pour les données d'environnement
      isLoadingEnvironmentData,
      environmentDataError,
      environmentGameData,

      // Variables pour les métiers
      isLoadingJobs,
      jobsError,
      jobChoicesData,
      jobCompletionPercentage,
      likedJobs,
      jobsStatistics,
      selectedJob,

      // Variables pour les accompagnants
      isLoadingObservers,
      observersError,
      observers,
      pendingInvitations,
      activeObservers,

      // Méthodes
      getSimpleSkillLevel,
      calculateLevel,
      getExpPercentage,
      getSkillsByCategory,
      getLevelColor,
      getSkillLevelName,
      getSkillIcon,
      formatDate,
      saveBio,
      addHobby,
      removeHobby,
      saveHobbies,
      showBadgeDetails,
      closeModal,
      selectCVStyle,
      selectCVColor,
      printCV,
      downloadCV,
      goToJobDiscovery,
      toggleSound,
      toggleHighContrast,
      increaseTextSize,
      decreaseTextSize,
      closeBadgeAnimation,
      dismissGuide,
      fetchUserProfile,
      refreshUserProfile,
      copyCodeToClipboard,

      // Méthodes pour les compétences
      loadSkills,
      refreshSkills,
      loadSkillsFromLocalStorage,
      combineSkillsData,
      determineSkillCategory,
      getSkillIdFromDisplayName,
      getSkillDisplayName,
      loadScenarioSkills,
      loadSkillsWheelData,

      // Fonctions pour les données de vitesse et d'environnement
      loadSpeedGameData,
      loadEnvironmentGameData,
      getTypingSkillLevel,
      getEnvironmentPreferences,
      getFeedbackText,
      goToEnvironmentGame,

      // Fonctions pour les métiers
      loadJobsData,
      refreshJobsData,
      loadJobsFromLocalStorage,
      getJobChoiceColor,
      getJobChoiceText,
      viewJobDetails,
      closeJobModal,
      removeJobFromLiked,

      // Méthodes pour le guide avatar (tutoriel)
      handleGuideOptionSelected,
      prepareGuideForRediscovery,
      resetGuideState,
      restartProfileTour,
      startProfileTour,
      nextTourStep,
      finishProfileTour,

      // Méthodes pour les accompagnants
      loadObservers,
      acceptInvitation,
      rejectInvitation,
      isFormationBadge,
      getBadgeImagePath,
      shouldUseBadgeImage,
      shouldEnlargeBadgeImage,
    }
  },
}
</script>

<style scoped>
/* Base Styles */
.profile-container {
  font-family: 'Comic Sans MS', 'Chalkboard SE', 'Marker Felt', sans-serif;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  color: #333;
  min-height: 100vh;
  position: relative;
}

/* Adaptations d'accessibilité */
.profile-container.high-contrast-mode {
  background-color: #000;
  color: #fff;
}

.profile-container.high-contrast-mode .tab-notification-badge {
  background-color: #ff9800;
  border: 3px solid #fff;
  box-shadow: 0 2px 4px rgba(255, 255, 255, 0.3);
}

.profile-container.text-small {
  font-size: 0.9rem;
}

.profile-container.text-large {
  font-size: 1.2rem;
}

/* Header */
.profile-header {
  margin-bottom: 30px;
  position: relative;
}

.main-title {
  font-size: 2.5rem;
  color: #3f51b5;
  text-align: center;
  margin-bottom: 10px;
}

.subtitle {
  font-size: 1.2rem;
  color: #666;
  text-align: center;
  margin: 0;
}

/* Guide Avatar Styles */
.guide-top-left {
  position: fixed;
  z-index: 1000;
}

/* Bouton d'actualisation des compétences */
.refresh-skills-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-left: 15px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.refresh-skills-btn:hover:not(:disabled) {
  background-color: #388e3c;
  transform: translateY(-2px);
}

.refresh-skills-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-icon {
  font-size: 0.9rem;
}

/* Indicateurs de chargement et d'erreur pour les compétences */
.loading-skills {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 15px;
  margin: 20px 0;
}

.skills-error {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
}

/* Légende des sources de compétences */
.skills-legend {
  display: flex;
  justify-content: center;
  gap: 25px;
  margin-bottom: 20px;
  padding: 10px;
  background-color: rgba(63, 81, 181, 0.05);
  border-radius: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #555;
}

/* Badges de source */
.source-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: bold;
  color: white;
  margin-left: 8px;
}

.source-badge.scenario {
  background-color: #ff9800;
}

.source-badge.wheel {
  background-color: #2196f3;
}

.source-badge.local {
  background-color: #9e9e9e;
}

/* Amélioration du nom de compétence avec badge */
.skill-name-simple {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

/* Statistiques des compétences */
.skills-stats {
  display: flex;
  justify-content: space-around;
  margin-top: 30px;
  padding: 20px;
  background-color: rgba(63, 81, 181, 0.05);
  border-radius: 15px;
}

.skills-stats .stat-item {
  text-align: center;
  padding: 10px;
}

.skills-stats .stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #3f51b5;
  margin-bottom: 5px;
}

.skills-stats .stat-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

/* Animation pour les nouvelles compétences */
.skill-item-simple {
  animation: slideInFromLeft 0.5s ease-out;
}

@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Amélioration de l'icône de compétence avec indicateur de source */
.skill-icon-simple {
  position: relative;
}

.skill-icon-simple::after {
  content: '';
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
  opacity: 0.7;
}

/* Message d'encouragement quand aucune compétence */
.empty-category-simple {
  text-align: center;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 10px;
  font-size: 1rem;
  color: #666;
  font-style: italic;
  position: relative;
  overflow: hidden;
}

.empty-category-simple::before {
  content: '🎮';
  position: absolute;
  top: 10px;
  right: 15px;
  font-size: 1.5rem;
  opacity: 0.3;
}

/* ========================================
  SECTION VITESSE DE FRAPPE
  ======================================== */

.typing-skill-section {
  background-color: #f8f9fa;
  border-radius: 15px;
  padding: 20px;
  margin-top: 30px;
  border-left: 4px solid #4caf50;
}

.typing-skill-card {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.typing-skill-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.typing-level-badge {
  padding: 8px 16px;
  border-radius: 25px;
  font-weight: bold;
  font-size: 0.9rem;
  text-align: center;
}

.typing-level-badge.level-1 {
  background-color: #ffebee;
  color: #f44336;
}

.typing-level-badge.level-2 {
  background-color: #fff3e0;
  color: #ff9800;
}

.typing-level-badge.level-3 {
  background-color: #e8f5e9;
  color: #4caf50;
}

.typing-level-badge.level-4 {
  background-color: #e3f2fd;
  color: #2196f3;
}

.typing-level-badge.level-5 {
  background-color: #f3e5f5;
  color: #9c27b0;
}

.typing-progress {
  font-size: 0.9rem;
  color: #666;
}

.typing-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.typing-metric {
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  gap: 12px;
}

.typing-metric .metric-icon {
  font-size: 1.5rem;
  width: 40px;
  text-align: center;
}

.typing-metric .metric-value {
  font-size: 1.4rem;
  font-weight: bold;
  color: #333;
}

.typing-metric .metric-label {
  font-size: 0.8rem;
  color: #666;
  margin-top: 2px;
}

.typing-skill-description {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
}

.skill-achievement {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #4caf50;
  font-weight: 500;
}

.skill-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ff9800;
  font-weight: 500;
}

.skill-encouragement {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2196f3;
  font-weight: 500;
}

.achievement-icon,
.progress-icon,
.encouragement-icon {
  font-size: 1.2rem;
}

/* Statistique de vitesse dans les stats globales */
.typing-stat {
  border-left: 4px solid #4caf50;
}

.typing-stat .stat-value {
  color: #4caf50;
}

/* ========================================
  SECTION ENVIRONNEMENT
  ======================================== */

.environment-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Bouton d'actualisation des données d'environnement */
.refresh-environment-btn {
  background-color: #2196f3;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-left: 15px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.refresh-environment-btn:hover:not(:disabled) {
  background-color: #1976d2;
  transform: translateY(-2px);
}

.refresh-environment-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Indicateurs de chargement et d'erreur pour l'environnement */
.loading-environment {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 15px;
  margin: 20px 0;
}

.environment-error {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
}

/* Résumé des préférences */
.environment-summary {
  background-color: #f8f9fa;
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 30px;
  border-left: 4px solid #2196f3;
}

.preferences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.preference-card {
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: transform 0.3s ease;
}

.preference-card:hover {
  transform: translateY(-2px);
}

.preference-card .preference-icon {
  font-size: 2rem;
  width: 50px;
  text-align: center;
}

.preference-card h4 {
  margin: 0 0 5px 0;
  color: #333;
  font-size: 1rem;
}

.preference-card p {
  margin: 0;
  color: #666;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Couleurs spécifiques pour chaque type de préférence */
.light-preference {
  border-left: 4px solid #ffb74d;
}

.noise-preference {
  border-left: 4px solid #81c784;
}

.social-preference {
  border-left: 4px solid #64b5f6;
}

.comfort-preference {
  border-left: 4px solid #ba68c8;
}

/* Environnements préférés */
.preferred-environments {
  margin-bottom: 30px;
}

.environments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 15px;
  margin-top: 20px;
}

.environment-card {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border-left: 4px solid #4caf50;
}

.environment-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
}

.environment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
  gap: 10px;
}

.environment-name {
  font-size: 1.2rem;
  color: #333;
  margin: 0;
  flex: 1;
}

.environment-feedback {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  white-space: nowrap;
}

.environment-feedback.feedback-very-calm {
  background-color: #e8f5e9;
  color: #4caf50;
}

.environment-feedback.feedback-focused {
  background-color: #e3f2fd;
  color: #2196f3;
}

.environment-feedback.feedback-comfortable {
  background-color: #fff3e0;
  color: #ff9800;
}

.environment-commentary {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 8px;
  font-style: italic;
}

.commentary-icon {
  font-size: 1rem;
  margin-top: 2px;
  color: #666;
}

.commentary-text {
  color: #555;
  line-height: 1.4;
  font-size: 0.95rem;
}

/* Conseils d'aménagement */
.environment-recommendations {
  background-color: #f9f9f9;
  border-radius: 15px;
  padding: 20px;
}

.recommendations-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.recommendation-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  background-color: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.recommendation-item .recommendation-icon {
  font-size: 1.5rem;
  width: 40px;
  text-align: center;
  margin-top: 5px;
}

.recommendation-content h4 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 1.1rem;
}

.recommendation-content p {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

/* État vide pour l'environnement */
.empty-environment {
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 15px;
  margin-bottom: 30px;
}

.empty-environment h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.empty-environment p {
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 10px;
  line-height: 1.5;
}

/* Progression de l'environnement */
.environment-progress {
  background-color: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  text-align: center;
  border-top: 3px solid #2196f3;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.progress-text {
  font-weight: 500;
  color: #333;
}

.completion-text {
  font-size: 0.9rem;
  color: #2196f3;
  font-weight: bold;
}

/* Contrôles d'accessibilité */
.accessibility-controls {
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  gap: 5px;
  z-index: 50;
}

.accessibility-button {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: none;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.accessibility-button:hover {
  background-color: #e0e0e0;
  transform: scale(1.1);
}

/* Navigation */
.navigation-tabs {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  margin-bottom: 30px;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 50px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.tab-button {
  padding: 10px 16px;
  border: 2px solid rgba(63, 81, 181, 0.25);
  background-color: #ffffff;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
  color: #4a4a4a;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex: 1 1 0;
  position: relative;
  height: 38px;
  justify-content: center;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.tab-button:hover {
  background-color: rgba(63, 81, 181, 0.08);
  border-color: rgba(63, 81, 181, 0.45);
  color: #2f3da0;
}

.tab-button.active {
  background-color: #3f51b5;
  color: #ffffff;
  border-color: #3f51b5;
  box-shadow: 0 4px 10px rgba(63, 81, 181, 0.25);
}

.tab-icon {
  font-size: 1.1rem;
  opacity: 0.95;
}

.tab-notification-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: #ff9800;
  color: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  animation: pulse-notification 2s infinite;
  z-index: 10;
}

.tab-button:focus-visible {
  outline: 3px solid rgba(63, 81, 181, 0.6);
  outline-offset: 2px;
}

@keyframes pulse-notification {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(255, 152, 0, 0.4);
  }
}

/* Sections de profil */
.profile-section {
  margin-bottom: 40px;
  background-color: white;
  border-radius: 20px;
  padding: 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  animation: fadeIn 0.5s ease;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  color: #3f51b5;
  margin-top: 0;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f5f5f5;
}

.section-icon {
  font-size: 1.8rem;
  margin-right: 10px;
}

/* Informations personnelles */
.personal-info-container {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}

.avatar-section {
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-container {
  position: relative;
  width: 220px;
  height: 120px;
  margin-bottom: 100px;
}

.user-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid #3f51b5;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.edit-avatar-button {
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: #3f51b5;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.edit-avatar-button:hover {
  transform: scale(1.1);
  background-color: #303f9f;
}

.level-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.level-badge {
  background-color: #4caf50;
  color: white;
  padding: 5px 15px;
  border-radius: 50px;
  font-weight: bold;
  margin-bottom: 10px;
  font-size: 0.9rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.exp-bar {
  width: 100%;
  height: 10px;
  background-color: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
}

.exp-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 5px;
  transition: width 0.5s ease;
}

.info-fields {
  flex: 1;
  min-width: 300px;
}

.info-field {
  margin-bottom: 20px;
}

.info-field label {
  display: block;
  font-weight: bold;
  color: #3f51b5;
  margin-bottom: 5px;
  font-size: 1.1rem;
}

.field-value {
  background-color: #f5f5f5;
  padding: 12px 15px;
  border-radius: 10px;
  color: #555;
  font-size: 1rem;
  line-height: 1.4;
}

.field-value.bio {
  min-height: 80px;
  white-space: pre-wrap;
}

/* Nouveau style pour le code de liaison - Version sobre et simple */
.code-liaison-field {
  margin: 20px 0;
}

.code-liaison-field label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  color: #3f51b5;
  margin-bottom: 12px;
}

.code-icon {
  font-size: 1.2rem;
}

.code-liaison-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.code-card {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 20px;
  border: 2px solid #e0e0e0;
  transition: border-color 0.3s ease;
}

.code-card:hover {
  border-color: #3f51b5;
}

.code-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 8px;
  text-align: center;
}

.code-value-display {
  font-family: 'Courier New', monospace;
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  text-align: center;
  letter-spacing: 4px;
  margin: 10px 0;
  padding: 12px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.copy-code-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background-color: #3f51b5;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-code-btn:hover {
  background-color: #303f9f;
}

.copy-code-btn:active {
  transform: scale(0.98);
}

.copy-code-btn.copied {
  background-color: #4caf50;
}

.copy-icon {
  font-size: 1.1rem;
}

.copy-text {
  font-size: 0.95rem;
}

.code-info-modern {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background-color: #e8eaf6;
  border-radius: 8px;
  border-left: 3px solid #3f51b5;
}

.info-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.code-info-modern p {
  margin: 0;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.5;
}

.edit-button {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 50px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.3s ease;
}

.edit-button:hover {
  background-color: #388e3c;
}

.hobbies-container {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
}

.hobby-tag {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 10px 16px;
  border-radius: 50px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(25, 118, 210, 0.1);
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.hobby-tag:hover {
  background-color: #bbdefb;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.hobby-tag:active {
  transform: translateY(0);
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
}

.hobby-tag::before {
  content: '';
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #1976d2;
  border-radius: 50%;
  margin-right: 2px;
}

/* Icônes spécifiques pour chaque loisir */
.hobby-tag[data-hobby='Jeux vidéo']::before {
  content: '🎮';
  background: none;
  width: auto;
  height: auto;
  margin-right: 0;
}

.hobby-tag[data-hobby='Musique']::before {
  content: '🎵';
  background: none;
  width: auto;
  height: auto;
  margin-right: 0;
}

.hobby-tag[data-hobby='Dessin']::before {
  content: '🎨';
  background: none;
  width: auto;
  height: auto;
  margin-right: 0;
}

.hobby-tag[data-hobby='Natation']::before {
  content: '🏊';
  background: none;
  width: auto;
  height: auto;
  margin-right: 0;
}

.add-hobby-button {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: #3f51b5;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-hobby-button:hover {
  background-color: #303f9f;
}

/* Compétences */
.skills-container {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}

.skills-chart-container {
  flex: 1;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.skills-wheel {
  width: 300px;
  height: 300px;
  margin-bottom: 20px;
}

.skills-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
}

.color-dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
}

.legend-label {
  font-size: 0.9rem;
  color: #555;
}

.skills-lists {
  flex: 2;
  min-width: 300px;
}

.skill-category {
  margin-bottom: 30px;
}

.category-title {
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.category-icon {
  margin-right: 10px;
}

.skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.skill-card {
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.skill-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
}

.skill-icon {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  margin-right: 12px;
  flex-shrink: 0;
  color: white;
}

.skill-info {
  flex: 1;
}

.skill-name {
  font-weight: bold;
  margin-bottom: 5px;
  color: #333;
  font-size: 0.95rem;
}

.skill-level {
  font-size: 0.8rem;
  margin-bottom: 5px;
  font-weight: bold;
}

.skill-bar {
  height: 5px;
  width: 100%;
  background-color: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
}

.skill-fill {
  height: 100%;
  transition: width 0.5s ease;
}

.empty-category {
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  background-color: #f5f5f5;
  border-radius: 10px;
  color: #999;
  font-style: italic;
}

/* Styles pour la section compétences simplifiée */
.skills-simplified {
  width: 100%;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 15px;
}

.skills-intro {
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
  font-weight: bold;
}

.skills-categories-simple {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.skill-category-simple {
  background-color: white;
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
}

.category-title-simple {
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 15px;
  color: #3f51b5;
}

.category-icon-large {
  font-size: 2rem;
  margin-right: 10px;
}

.skills-list-simple {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.skill-item-simple {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  transition: transform 0.3s;
}

.skill-item-simple:hover {
  transform: translateY(-3px);
}

.skill-icon-simple {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-right: 15px;
  color: white;
}

.skill-info-simple {
  flex: 1;
}

.skill-name-simple {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.skill-stars {
  display: flex;
  gap: 5px;
  margin-bottom: 5px;
}

.skill-star {
  font-size: 1.5rem;
  color: #e0e0e0;
}

.skill-star.filled {
  color: #ffd700; /* Couleur or pour les étoiles remplies */
}

.skill-level-simple {
  font-size: 1rem;
  padding: 4px 12px;
  border-radius: 20px;
  display: inline-block;
  font-weight: bold;
}

/* Couleurs pour les différents niveaux simplifiés */
.level-debut {
  background-color: #e3f2fd;
  color: #1976d2;
}

.level-progres {
  background-color: #e8f5e9;
  color: #388e3c;
}

.level-fort {
  background-color: #fff3e0;
  color: #e64a19;
}

.level-super {
  background-color: #f3e5f5;
  color: #7b1fa2;
}

.empty-category-simple {
  text-align: center;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 10px;
  font-size: 1.1rem;
  color: #757575;
}

/* Bouton d'actualisation des métiers */
.refresh-jobs-btn {
  background-color: #ff9800;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  margin-left: 15px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.refresh-jobs-btn:hover:not(:disabled) {
  background-color: #f57c00;
  transform: translateY(-2px);
}

.refresh-jobs-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-icon {
  font-size: 0.9rem;
}

/* Indicateurs de chargement et d'erreur pour les métiers */
.loading-jobs {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  background-color: #f9f9f9;
  border-radius: 15px;
  margin: 20px 0;
}

.jobs-error {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
}

.error-message {
  color: #f44336;
  margin: 0 0 15px 0;
  font-size: 1rem;
}

/* Container principal des carrières */
.careers-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Statistiques des métiers */
.jobs-statistics {
  background-color: #f8f9fa;
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 30px;
  border-left: 4px solid #ff9800;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.stat-card {
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
}

.stat-card.liked {
  border-left: 4px solid #4caf50;
}

.stat-card.completion {
  border-left: 4px solid #2196f3;
}

.stat-value {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.stat-card.liked .stat-value {
  color: #4caf50;
}

.stat-card.completion .stat-value {
  color: #2196f3;
}

.stat-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

/* Barre de progression pour les métiers */
.progress-container {
  margin-top: 15px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background-color: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff9800, #ffb74d);
  border-radius: 6px;
  transition: width 0.5s ease;
}

.progress-text {
  text-align: center;
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

/* ========================================
  MASQUAGE DES IMAGES ET REMPLACEMENT
  ======================================== */

/* Masquer complètement les containers d'images des métiers */
.job-image-container,
.job-image,
.liked-job-media {
  display: none !important;
}

/* Badge métier aimé (masqué car sur image) */
.job-liked-badge {
  display: none !important;
}

/* ========================================
  SECTION MÉTIERS AIMÉS
  ======================================== */

.liked-jobs-section {
  margin-bottom: 30px;
}

/* Grille des métiers */
.jobs-grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(220px, 1fr)
  ); /* Réduit de 280px à 220px */
  gap: 15px; /* Réduit de 20px à 15px */
  margin-bottom: 20px;
}

/* Cartes de métiers sans images */
.job-card {
  background-color: white;
  border-radius: 12px; /* Réduit de 15px à 12px */
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05); /* Ombre plus légère */
  overflow: hidden;
  transition: all 0.3s ease;
  padding: 15px; /* Réduit de 20px à 15px */
  border-left: 4px solid #ff6b6b; /* Réduit de 5px à 4px */
  position: relative;
}

.job-card:hover {
  transform: translateY(-3px); /* Réduit de -5px à -3px */
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1); /* Ombre hover plus légère */
  animation: jobCardPulse 0.6s ease-in-out;
}

/* Icône de remplacement pour les métiers */
.job-card::before {
  content: '💼';
  font-size: 2rem; /* Réduit de 2.5rem à 2rem */
  display: block;
  text-align: center;
  margin-bottom: 10px; /* Réduit de 15px à 10px */
  opacity: 0.7;
}

/* Icônes spécifiques par métier */
.job-card[data-job='Webdesigner']::before {
  content: '💻';
  font-size: 2rem;
}
.job-card[data-job='Médecin']::before {
  content: '⚕️';
  font-size: 2rem;
}
.job-card[data-job='Professeur']::before {
  content: '📚';
  font-size: 2rem;
}
.job-card[data-job='Fleuriste']::before {
  content: '🌸';
  font-size: 2rem;
}
.job-card[data-job='Ingénieur']::before {
  content: '⚙️';
  font-size: 2rem;
}
.job-card[data-job='Cuisinier']::before {
  content: '👨‍🍳';
  font-size: 2rem;
}
.job-card[data-job='Architecte']::before {
  content: '🏗️';
  font-size: 2rem;
}
.job-card[data-job='Infirmier']::before {
  content: '👩‍⚕️';
  font-size: 2rem;
}
.job-card[data-job='Avocat']::before {
  content: '⚖️';
  font-size: 2rem;
}
.job-card[data-job='Photographe']::before {
  content: '📷';
  font-size: 2rem;
}
.job-card[data-job='Musicien']::before {
  content: '🎵';
  font-size: 2rem;
}
.job-card[data-job='Journaliste']::before {
  content: '📰';
  font-size: 2rem;
}

/* Info des métiers */
.job-info {
  padding: 0;
  width: 100%;
}

.job-title {
  font-size: 1.1rem; /* Réduit de 1.3rem à 1.1rem */
  color: #ff6b6b;
  text-align: center;
  margin: 0 0 10px 0; /* Réduit de 15px à 10px */
  font-weight: bold;
  line-height: 1.2; /* Améliore la compacité */
}

/* Compétences des métiers */
.job-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px; /* Réduit de 8px à 6px */
  margin-bottom: 15px; /* Réduit de 20px à 15px */
}

.job-skill-tag {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 4px 8px; /* Réduit de 6px 12px à 4px 8px */
  border-radius: 15px; /* Réduit de 20px à 15px */
  font-size: 0.75rem; /* Réduit de 0.85rem à 0.75rem */
  font-weight: 500;
  line-height: 1.2;
}

.job-skill-more {
  background-color: #e0e0e0;
  color: #757575;
  padding: 4px 8px; /* Réduit de 6px 12px à 4px 8px */
  border-radius: 15px; /* Réduit de 20px à 15px */
  font-size: 0.75rem; /* Réduit de 0.85rem à 0.75rem */
}

/* Bouton voir détails */
.view-job-button {
  display: block;
  width: 100%;
  padding: 8px 0; /* Réduit de 12px à 8px */
  background-color: #ff6b6b;
  color: white;
  border: none;
  border-radius: 20px; /* Réduit de 25px à 20px */
  font-size: 0.85rem; /* Réduit de 0.95rem à 0.85rem */
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.view-job-button:hover {
  background-color: #ff5252;
  transform: translateY(-1px); /* Réduit de -2px à -1px */
}

/* ========================================
  MÉTIERS AIMÉS (VERSION SIMPLIFIÉE)
  ======================================== */

.liked-job-item {
  background-color: #f8f9fa;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  padding: 20px;
  border-left: 5px solid #ff6b6b;
  position: relative;
}

.liked-job-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  animation: jobCardPulse 0.6s ease-in-out;
}

/* Icônes pour les métiers aimés */
.liked-job-item::before {
  content: '💼';
  font-size: 2.5rem;
  display: block;
  text-align: center;
  margin-bottom: 15px;
  opacity: 0.7;
}

.liked-job-item[data-job='Webdesigner']::before {
  content: '💻';
}
.liked-job-item[data-job='Médecin']::before {
  content: '⚕️';
}
.liked-job-item[data-job='Professeur']::before {
  content: '📚';
}
.liked-job-item[data-job='Fleuriste']::before {
  content: '🌸';
}
.liked-job-item[data-job='Ingénieur']::before {
  content: '⚙️';
}
.liked-job-item[data-job='Cuisinier']::before {
  content: '👨‍🍳';
}
.liked-job-item[data-job='Architecte']::before {
  content: '🏗️';
}

.liked-job-info {
  padding: 0;
  width: 100%;
}

.liked-job-info h3 {
  font-size: 1.3rem;
  color: #333;
  margin: 0 0 15px 0;
  font-weight: bold;
  text-align: center;
}

/* ========================================
  ÉTATS VIDES
  ======================================== */

.empty-liked-jobs,
.empty-job-interests {
  text-align: center;
  padding: 40px 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 15px;
  margin-bottom: 30px;
}

.empty-liked-jobs h3,
.empty-job-interests h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.empty-liked-jobs p,
.empty-job-interests p {
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 10px;
  line-height: 1.5;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  color: #9e9e9e;
}

.action-button {
  background-color: #ff9800;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.action-button:hover {
  background-color: #f57c00;
  transform: translateY(-3px);
}

/* ========================================
  SECTION TOUS LES CHOIX DE MÉTIERS
  ======================================== */

.all-job-choices {
  background-color: #f9f9f9;
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 30px;
}

.job-choices-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 10px;
}

.job-choice-item {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
}

.job-choice-item:hover {
  transform: translateY(-2px);
}

.job-choice-item.like {
  border-left: 4px solid #4caf50;
}

.job-choice-item.dislike {
  border-left: 4px solid #f44336;
}

.job-choice-item.unknown {
  border-left: 4px solid #ff9800;
}

.job-choice-indicator {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 1.2rem;
  color: white;
}

.job-choice-info {
  flex: 1;
}

.job-choice-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.job-choice-status {
  font-size: 0.8rem;
  font-weight: 500;
}

.job-modal {
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.4s ease;
}

/* Contenu de la modale */
.job-detail-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

/* Icône du métier en grand */
.job-detail-icon {
  width: 120px;
  height: 120px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
  box-shadow: 0 8px 20px rgba(255, 107, 107, 0.3);
  position: relative;
  overflow: hidden;
}

.job-detail-icon::before {
  content: '💼';
  font-size: 4rem;
  color: white;
  z-index: 2;
  position: relative;
}

/* Icônes spécifiques pour les métiers dans la modale */
.job-detail-icon[data-job='Webdesigner']::before {
  content: '💻';
}
.job-detail-icon[data-job='Médecin']::before {
  content: '⚕️';
}
.job-detail-icon[data-job='Professeur']::before {
  content: '📚';
}
.job-detail-icon[data-job='Fleuriste']::before {
  content: '🌸';
}
.job-detail-icon[data-job='Ingénieur']::before {
  content: '⚙️';
}
.job-detail-icon[data-job='Cuisinier']::before {
  content: '👨‍🍳';
}
.job-detail-icon[data-job='Architecte']::before {
  content: '🏗️';
}
.job-detail-icon[data-job='Infirmier']::before {
  content: '👩‍⚕️';
}
.job-detail-icon[data-job='Avocat']::before {
  content: '⚖️';
}
.job-detail-icon[data-job='Photographe']::before {
  content: '📷';
}
.job-detail-icon[data-job='Musicien']::before {
  content: '🎵';
}
.job-detail-icon[data-job='Journaliste']::before {
  content: '📰';
}

/* Effet brillant sur l'icône */
.job-detail-icon::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  animation: shine 2s infinite;
}

@keyframes shine {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

/* Titre du métier */
.job-detail-title {
  font-size: 2rem;
  color: #ff6b6b;
  margin: 0 0 30px 0;
  font-weight: bold;
}

/* Sections de contenu */
.job-detail-description,
.job-detail-skills,
.job-detail-info {
  width: 100%;
  margin-bottom: 25px;
  text-align: left;
}

.job-detail-description h3,
.job-detail-skills h3,
.job-detail-info h3 {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #ff6b6b;
  display: flex;
  align-items: center;
  gap: 10px;
}

.job-detail-description h3::before {
  content: '📝';
  font-size: 1.5rem;
}

.job-detail-skills h3::before {
  content: '🎯';
  font-size: 1.5rem;
}

.job-detail-info h3::before {
  content: 'ℹ️';
  font-size: 1.5rem;
}

/* Description du métier */
.job-detail-description p {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 12px;
  border-left: 4px solid #ff6b6b;
  margin: 0;
}

/* Grille des compétences */
.job-skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-top: 15px;
}

.job-skill-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  color: #1976d2;
  padding: 12px 16px;
  border-radius: 25px;
  font-weight: 500;
  box-shadow: 0 3px 8px rgba(25, 118, 210, 0.2);
  transition: all 0.3s ease;
  border: 1px solid rgba(25, 118, 210, 0.2);
}

.job-skill-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 12px rgba(25, 118, 210, 0.3);
  background: linear-gradient(135deg, #bbdefb, #90caf9);
}

.job-skill-badge .skill-icon {
  font-size: 1.2rem;
}

.job-skill-badge .skill-text {
  font-size: 0.95rem;
}

/* Informations supplémentaires */
.job-info-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 15px;
}

.job-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  border-left: 4px solid #4caf50;
}

.job-info-item .info-label {
  font-weight: bold;
  color: #333;
  font-size: 0.95rem;
}

.job-info-item .info-value {
  color: #4caf50;
  font-weight: 500;
  font-size: 0.95rem;
}

/* Actions de la modale */
.job-modal-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.remove-button {
  background-color: #f44336;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.remove-button:hover {
  background-color: #d32f2f;
  transform: translateY(-2px);
}

.close-modal-button {
  background-color: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.close-modal-button:hover {
  background-color: #388e3c;
  transform: translateY(-2px);
}

.btn-icon {
  font-size: 1.1rem;
}

.btn-text {
  font-size: 0.95rem;
  font-weight: 500;
}

/* Animation d'entrée pour les éléments */
.job-detail-description,
.job-detail-skills,
.job-detail-info {
  animation: slideInFromBottom 0.4s ease-out;
}

.job-detail-description {
  animation-delay: 0.1s;
}

.job-detail-skills {
  animation-delay: 0.2s;
}

.job-detail-info {
  animation-delay: 0.3s;
}

/* Animation pour les badges de compétences */
.job-skill-badge {
  animation: slideInFromLeft 0.3s ease-out;
}

.job-skill-badge:nth-child(1) {
  animation-delay: 0.1s;
}
.job-skill-badge:nth-child(2) {
  animation-delay: 0.2s;
}
.job-skill-badge:nth-child(3) {
  animation-delay: 0.3s;
}
.job-skill-badge:nth-child(4) {
  animation-delay: 0.4s;
}
.job-skill-badge:nth-child(5) {
  animation-delay: 0.5s;
}
.job-skill-badge:nth-child(6) {
  animation-delay: 0.6s;
}

/* Responsive design pour la modale */
@media (max-width: 768px) {
  .job-modal {
    padding: 20px;
    width: 95%;
  }

  .job-detail-icon {
    width: 100px;
    height: 100px;
  }

  .job-detail-icon::before {
    font-size: 3rem;
  }

  .job-detail-title {
    font-size: 1.6rem;
  }

  .job-skills-grid {
    grid-template-columns: 1fr;
  }

  .job-modal-actions {
    flex-direction: column;
    gap: 10px;
  }

  .modal-button {
    width: 100%;
    justify-content: center;
  }

  /* Responsive pour le code de liaison */
  .code-card {
    padding: 16px;
  }

  .code-value-display {
    font-size: 1.3rem;
    letter-spacing: 3px;
  }

  .copy-code-btn {
    font-size: 0.9rem;
    padding: 10px 14px;
  }
}

@media (max-width: 480px) {
  .job-modal {
    padding: 15px;
  }

  .job-detail-icon {
    width: 80px;
    height: 80px;
  }

  .job-detail-icon::before {
    font-size: 2.5rem;
  }

  .job-detail-title {
    font-size: 1.4rem;
  }

  .job-detail-description h3,
  .job-detail-skills h3,
  .job-detail-info h3 {
    font-size: 1.1rem;
  }

  .job-skill-badge {
    padding: 10px 12px;
    font-size: 0.9rem;
  }

  /* Responsive mobile pour le code de liaison */
  .code-card {
    padding: 14px;
  }

  .code-value-display {
    font-size: 1.1rem;
    letter-spacing: 2px;
    padding: 10px;
  }

  .copy-code-btn {
    font-size: 0.85rem;
    padding: 9px 12px;
  }

  .code-info-modern {
    padding: 10px 12px;
  }

  .code-info-modern p {
    font-size: 0.85rem;
  }

  .job-info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

/* ========================================
  COMPÉTENCES PROFESSIONNELLES
  ======================================== */

.career-skills-match {
  background-color: #f9f9f9;
  border-radius: 15px;
  padding: 20px;
}

.career-skills-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.career-skill-card {
  display: flex;
  align-items: center;
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.career-skill-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e3f2fd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-right: 12px;
  flex-shrink: 0;
}

.career-skill-info {
  flex: 1;
}

.career-skill-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.career-skill-match {
  font-size: 0.8rem;
  color: #4caf50;
}

.empty-career-skills {
  grid-column: 1 / -1;
  text-align: center;
  padding: 15px;
  color: #999;
  font-style: italic;
}

/* ========================================
  SECTIONS ET TITRES
  ======================================== */

.subsection-title {
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.subsection-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background-color: #ff9800;
  border-radius: 2px;
}

/* ========================================
  ANIMATIONS
  ======================================== */

.stat-card,
.job-choice-item {
  animation: slideInFromBottom 0.3s ease-out;
}

@keyframes slideInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes jobCardPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
}

/* Préférences sensorielles */
.sensory-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.sensory-categories {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.sensory-category {
  background-color: #f9f9f9;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.sensory-preferences {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.preference-item {
  display: flex;
  flex-direction: column;
}

.preference-label {
  font-weight: bold;
  color: #555;
  margin-bottom: 5px;
}

.preference-value {
  background-color: white;
  padding: 10px;
  border-radius: 8px;
  color: #333;
}

.sensory-recommendations {
  background-color: #f9f9f9;
  border-radius: 15px;
  padding: 20px;
}

.recommendation-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.recommendation-item {
  display: flex;
  background-color: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
}

.recommendation-icon {
  font-size: 1.5rem;
  margin-right: 15px;
  color: #ff9800;
}

.recommendation-text {
  flex: 1;
  color: #555;
}

.empty-recommendations {
  text-align: center;
  padding: 15px;
  color: #999;
  font-style: italic;
}

/* CV et Présentation */
.cv-container {
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
}

.cv-preview {
  flex: 2;
  min-width: 300px;
  background-color: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.cv-preview {
  /* Utiliser la variable CSS pour la couleur principale */
  --cv-color: #2196f3; /* Couleur par défaut */
  transition: all 0.3s ease;
}

/* Appliquer la couleur principale aux éléments du CV */
.cv-name {
  color: var(--cv-color);
}

.cv-section-title {
  color: var(--cv-color);
  border-bottom-color: var(--cv-color);
}

/* Style Simple (par défaut) */
.cv-style-simple {
  font-family: 'Arial', sans-serif;
  padding: 30px;
}

.cv-style-simple .cv-section-title {
  font-size: 1.3rem;
  border-bottom: 1px solid var(--cv-color);
  padding-bottom: 8px;
  color: var(--cv-color);
}

.cv-style-simple .cv-name {
  color: var(--cv-color);
}

.cv-style-simple .cv-job-name {
  color: var(--cv-color);
}

.cv-style-simple .cv-skill-item {
  border-left: 3px solid var(--cv-color);
}

/* Style Créatif */
.cv-style-creative {
  font-family: 'Comic Sans MS', 'Chalkboard SE', sans-serif;
  padding: 25px;
  background-color: #f9f9f9;
  border-radius: 20px;
}

.cv-style-creative .cv-header {
  background-color: var(--cv-color);
  margin: -25px -25px 20px -25px;
  padding: 25px;
  border-radius: 20px 20px 0 0;
  text-align: center;
}

.cv-style-creative .cv-name {
  color: white;
  font-size: 2rem;
}

.cv-style-creative .cv-contact {
  color: rgba(255, 255, 255, 0.9);
}

.cv-style-creative .cv-section-title {
  background-color: rgba(0, 0, 0, 0.05);
  border: none;
  padding: 10px 15px;
  border-radius: 10px;
  font-size: 1.2rem;
}

.cv-style-creative .cv-skill-item,
.cv-style-creative .cv-job-item,
.cv-style-creative .cv-bio {
  border-radius: 15px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid var(--cv-color);
}

/* Style Professionnel */
.cv-style-professional {
  font-family: 'Calibri', 'Helvetica', sans-serif;
  padding: 30px;
  border: 1px solid #e0e0e0;
}

.cv-style-professional .cv-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
  border-bottom: 2px solid var(--cv-color);
}

.cv-style-professional .cv-name {
  font-size: 1.8rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.cv-style-professional .cv-section-title {
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 1.1rem;
  font-weight: bold;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.cv-style-professional .cv-section-title::before {
  content: '';
  display: inline-block;
  width: 12px;
  height: 12px;
  background-color: var(--cv-color);
  margin-right: 10px;
}

.cv-style-professional .cv-skill-item,
.cv-style-professional .cv-job-item {
  background-color: white;
  border: 1px solid #e0e0e0;
  padding: 12px;
}

.cv-style-professional .cv-bio {
  background-color: white;
  border: 1px solid #e0e0e0;
  padding: 15px;
}

.cv-header {
  text-align: center;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f5f5f5;
}

.cv-name {
  font-size: 1.8rem;
  color: #3f51b5;
  margin: 0 0 10px 0;
}

.cv-contact {
  color: #757575;
}

.cv-section {
  margin-bottom: 25px;
}

.cv-section-title {
  color: #3f51b5;
  font-size: 1.3rem;
  margin: 0 0 15px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.cv-strength-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
}

.cv-skill-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 10px;
  padding: 15px 10px;
}

.cv-skill-icon {
  font-size: 1.8rem;
  margin-bottom: 10px;
}

.cv-skill-name {
  text-align: center;
  font-size: 0.9rem;
  font-weight: bold;
}

.cv-jobs-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.cv-job-item {
  background-color: #f5f5f5;
  padding: 12px 15px;
  border-radius: 10px;
}

.cv-job-name {
  font-weight: bold;
  color: #3f51b5;
}

.cv-bio {
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 10px;
  color: #555;
  line-height: 1.5;
}

.cv-empty-section {
  text-align: center;
  padding: 15px;
  background-color: #f5f5f5;
  border-radius: 10px;
  color: #999;
  font-style: italic;
}

.cv-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 25px;
}

.cv-button {
  padding: 10px 20px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.print-button {
  background-color: #4caf50;
  color: white;
}

.print-button:hover {
  background-color: #388e3c;
}

.download-button {
  background-color: #2196f3;
  color: white;
}

.download-button:hover {
  background-color: #1976d2;
}

.cv-customize {
  flex: 1;
  min-width: 250px;
  background-color: #f9f9f9;
  border-radius: 15px;
  padding: 20px;
}

.customize-title {
  font-size: 1.3rem;
  color: #3f51b5;
  margin-top: 0;
  margin-bottom: 20px;
  text-align: center;
}

.customize-options {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.customize-option {
  margin-bottom: 20px;
}

.customize-option label {
  display: block;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.style-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.style-button {
  background-color: #e0e0e0;
  padding: 8px 15px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.style-button:hover {
  background-color: #c0c0c0;
}

.style-button.active {
  background-color: #3f51b5;
  color: white;
}

.color-options {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.color-button {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
}

.color-button:hover {
  transform: scale(1.1);
}

.color-button.active {
  border-color: #333;
  transform: scale(1.1);
}

/* Modales */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background-color: white;
  border-radius: 20px;
  padding: 25px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.4s ease;
}

.modal-title {
  font-size: 1.5rem;
  color: #3f51b5;
  margin-top: 0;
  margin-bottom: 20px;
  text-align: center;
}

.bio-textarea {
  width: 100%;
  min-height: 150px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  margin-bottom: 20px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
}

.modal-button {
  padding: 10px 20px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.cancel-button {
  background-color: #e0e0e0;
  color: #333;
}

.cancel-button:hover {
  background-color: #c0c0c0;
}

.save-button {
  background-color: #4caf50;
  color: white;
}

.save-button:hover {
  background-color: #388e3c;
}

.hobby-editor {
  margin-bottom: 20px;
}

.current-hobbies {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.hobby-editor-item {
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 8px 15px;
  border-radius: 50px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.remove-hobby-button {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #e0e0e0;
  border: none;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-hobby-button:hover {
  background-color: #f44336;
  color: white;
}

.add-hobby-form {
  display: flex;
  gap: 10px;
}

.hobby-input {
  flex: 1;
  padding: 10px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 50px;
  font-size: 0.9rem;
}

.add-button {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-button:hover {
  background-color: #388e3c;
}

.avatar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

.avatar-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  border-radius: 10px;
  padding: 10px;
}

.avatar-option:hover {
  background-color: #f5f5f5;
}

.avatar-option.active {
  border-color: #3f51b5;
  background-color: #e8eaf6;
}

.avatar-preview {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 8px;
}

.avatar-name {
  font-size: 0.9rem;
  color: #555;
  text-align: center;
}

/* Détails du badge */
.badge-modal {
  background-color: white;
  border-radius: 20px;
  padding: 25px;
  max-width: 400px;
  width: 90%;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  animation: scaleIn 0.4s ease;
}

.close-button {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: #f5f5f5;
  border: none;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #e0e0e0;
}

.badge-detail-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.badge-detail-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.badge-detail-emoji {
  font-size: 3rem;
}

.badge-detail-title {
  font-size: 1.6rem;
  color: #3f51b5;
  margin: 0 0 15px 0;
}

.badge-detail-description {
  color: #555;
  margin-bottom: 20px;
  line-height: 1.5;
}

.badge-achievement {
  background-color: #e8f5e9;
  padding: 15px;
  border-radius: 10px;
  width: 100%;
  margin-bottom: 20px;
}

.achievement-date {
  font-weight: bold;
  color: #4caf50;
  margin-bottom: 5px;
}

.achievement-game {
  color: #757575;
}

.badge-locked-info {
  background-color: #fff8e1;
  padding: 15px;
  border-radius: 10px;
  width: 100%;
}

.badge-hint {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.hint-icon {
  font-size: 1.5rem;
  color: #ff9800;
}

/* Animation de déblocage du badge */
.badge-unlock-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  animation: fadeIn 0.5s ease-out;
}

.badge-unlock-animation {
  background-color: white;
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 0 30px rgba(63, 81, 181, 0.6);
  animation: scaleIn 0.5s ease-out;
}

.badge-unlock-animation .badge-icon {
  font-size: 5rem;
  margin-bottom: 20px;
  animation: pulse 2s infinite;
}

.badge-unlock-animation h2 {
  color: #3f51b5;
  font-size: 2rem;
  margin-bottom: 10px;
}

.badge-unlock-animation h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 15px;
}

.badge-unlock-animation p {
  color: #666;
  margin-bottom: 20px;
}

.close-animation-btn {
  background-color: #3f51b5;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-animation-btn:hover {
  background-color: #303f9f;
  transform: scale(1.05);
}

/* Guide contextuel */
.profile-guide {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: flex-start;
  max-width: 400px;
  z-index: 900;
  animation: slideUp 0.5s ease;
}

.guide-character {
  margin-right: 10px;
}

.guide-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid #ff9800;
  background-color: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.guide-bubble {
  background-color: white;
  border-radius: 15px;
  padding: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
}

.guide-bubble:before {
  content: '';
  position: absolute;
  left: -10px;
  top: 20px;
  border-width: 10px 10px 10px 0;
  border-style: solid;
  border-color: transparent white transparent transparent;
}

.guide-bubble p {
  margin: 0 0 15px 0;
  color: #555;
  line-height: 1.5;
}

.guide-dismiss-button {
  background-color: #ff9800;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 50px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.guide-dismiss-button:hover {
  background-color: #f57c00;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* Indicateur de chargement et erreurs */
.loading-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(63, 81, 181, 0.3);
  border-top: 4px solid #3f51b5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.profile-error {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin: 20px 0;
  text-align: center;
}

.error-message {
  color: #f44336;
  margin: 0 0 15px 0;
  font-size: 1rem;
}

.refresh-profile-btn {
  background-color: rgba(63, 81, 181, 0.2);
  border: 1px solid rgba(63, 81, 181, 0.5);
  color: #3f51b5;
  padding: 10px 20px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  margin: 0 auto;
}

.refresh-profile-btn:hover:not(:disabled) {
  background-color: rgba(63, 81, 181, 0.3);
  transform: translateY(-2px);
}

.refresh-profile-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-icon {
  font-size: 1.2rem;
}

/* ========================================
  RESPONSIVE DESIGN POUR LES NOUVELLES SECTIONS
  ======================================== */

@media (max-width: 768px) {
  .personal-info-container {
    flex-direction: column;
    align-items: center;
  }

  .avatar-section {
    width: 100%;
    max-width: 200px;
    margin-bottom: 20px;
  }

  .navigation-tabs {
    justify-content: flex-start;
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 10px 5px;
    gap: 5px;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .navigation-tabs::-webkit-scrollbar {
    display: none; /* Chrome, Safari */
  }

  .tab-button {
    padding: 8px 15px;
    white-space: nowrap;
    flex: 0 0 auto;
  }

  .skills-chart-container {
    width: 100%;
  }

  .cv-container {
    flex-direction: column;
  }

  .profile-guide {
    bottom: 10px;
    right: 10px;
    max-width: 300px;
  }

  .guide-avatar {
    width: 50px;
    height: 50px;
  }

  .badge-modal,
  .modal-content {
    max-width: 90%;
    padding: 15px;
  }

  .typing-skill-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .typing-metrics {
    grid-template-columns: 1fr;
  }

  .preferences-grid {
    grid-template-columns: 1fr;
  }

  .environments-grid {
    grid-template-columns: 1fr;
  }

  .environment-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .recommendations-list {
    gap: 15px;
  }

  .recommendation-item {
    flex-direction: column;
    text-align: center;
  }

  .recommendation-item .recommendation-icon {
    width: auto;
    margin-top: 0;
  }

  .stats-grid {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 10px;
  }

  .jobs-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .job-choices-grid {
    grid-template-columns: 1fr;
    max-height: 250px;
  }

  .refresh-jobs-btn {
    margin-left: 10px;
    padding: 4px 8px;
    font-size: 0.7rem;
  }

  .subsection-title {
    font-size: 1.1rem;
  }

  .job-card,
  .liked-job-item {
    padding: 15px;
  }

  .job-card::before,
  .liked-job-item::before {
    font-size: 2rem;
    margin-bottom: 10px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 1.5rem;
  }

  .skills-grid,
  .jobs-grid,
  .badge-grid,
  .career-skills-grid {
    grid-template-columns: 1fr;
  }

  .sensory-categories {
    grid-template-columns: 1fr;
  }

  .modal-content {
    padding: 15px;
  }

  .profile-guide {
    max-width: 90%;
    left: 0;
    right: 0;
    margin: 0 auto;
  }

  .typing-skill-section,
  .environment-summary,
  .environment-recommendations {
    padding: 15px;
  }

  .typing-skill-card {
    padding: 15px;
  }

  .typing-metric {
    padding: 12px;
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }

  .environment-card {
    padding: 15px;
  }

  .environment-commentary {
    padding: 10px;
  }

  .progress-info {
    flex-direction: column;
    gap: 8px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .job-choice-item {
    padding: 10px;
  }

  .job-choice-indicator {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }

  .empty-liked-jobs,
  .empty-job-interests {
    padding: 30px 15px;
  }

  .job-card,
  .liked-job-item {
    padding: 12px;
  }

  .job-title {
    font-size: 1.1rem;
  }

  .job-skills {
    gap: 6px;
  }

  .job-skill-tag,
  .job-skill-more {
    font-size: 0.8rem;
    padding: 4px 8px;
  }

  .career-skills-grid {
    grid-template-columns: 1fr;
  }
}

/* ========================================
  SECTION ACCOMPAGNANTS (ONGLET DÉDIÉ)
  ======================================== */

.accompanists-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Chargement et erreur */
.loading-observers {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px;
  background-color: #f9f9f9;
  border-radius: 15px;
  margin: 20px 0;
}

.observers-error {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 30px;
  margin: 20px 0;
  text-align: center;
}

.refresh-observers-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  margin-top: 15px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.refresh-observers-btn:hover {
  background-color: #388e3c;
  transform: translateY(-2px);
}

/* Titres de sections */
.subsection-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #3f51b5;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-description {
  color: #666;
  font-size: 1rem;
  margin-bottom: 20px;
  line-height: 1.6;
}

.invitation-icon,
.active-icon {
  font-size: 1.5rem;
}

.count-badge {
  background-color: #3f51b5;
  color: white;
  font-size: 0.9rem;
  padding: 4px 12px;
  border-radius: 20px;
  margin-left: auto;
}

/* Invitations en attente */
.pending-invitations-section {
  background-color: #fff9e6;
  border-radius: 15px;
  padding: 25px;
  border-left: 4px solid #ff9800;
}

.invitations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.invitation-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.invitation-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(255, 152, 0, 0.25);
  border-color: #ff9800;
}

.invitation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.invitation-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ff9800, #ffb74d);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
  box-shadow: 0 3px 8px rgba(255, 152, 0, 0.3);
}

.invitation-badge {
  background-color: #ff9800;
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.invitation-info {
  margin-bottom: 20px;
}

.observer-name {
  font-size: 1.3rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.observer-role {
  margin-bottom: 8px;
}

.role-badge-pending {
  display: inline-block;
  background-color: #fff3e0;
  color: #ff9800;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 600;
}

.observer-email {
  font-size: 0.9rem;
  color: #888;
  margin-bottom: 10px;
}

.invitation-date,
.observer-connection-date {
  font-size: 0.85rem;
  color: #999;
  display: flex;
  align-items: center;
  gap: 5px;
}

.date-icon {
  font-size: 1rem;
}

.invitation-actions {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.accept-btn,
.reject-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.accept-btn {
  background-color: #4caf50;
  color: white;
}

.accept-btn:hover {
  background-color: #388e3c;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.reject-btn {
  background-color: #f44336;
  color: white;
}

.reject-btn:hover {
  background-color: #d32f2f;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

/* Accompagnants actifs */
.active-observers-section {
  background-color: #f8f9fa;
  border-radius: 15px;
  padding: 25px;
  border-left: 4px solid #4caf50;
}

.observers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.observer-card {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.observer-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-color: #4caf50;
}

.observer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.observer-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3f51b5, #5c6bc0);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
  box-shadow: 0 3px 8px rgba(63, 81, 181, 0.3);
}

.observer-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: #4caf50;
  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #4caf50;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.observer-details {
  margin-top: 10px;
}

.observer-details .observer-name {
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.role-badge {
  display: inline-block;
  background-color: #e8eaf6;
  color: #3f51b5;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 600;
}

.observer-details .observer-email {
  margin-top: 8px;
  margin-bottom: 10px;
}

.observer-details .observer-connection-date {
  margin-top: 12px;
}

.observer-notes {
  margin-top: 15px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #555;
  border-left: 3px solid #4caf50;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.notes-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

/* État vide */
.empty-accompanists {
  text-align: center;
  padding: 60px 40px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 20px;
  position: relative;
  overflow: hidden;
}

.empty-illustration {
  position: relative;
  margin-bottom: 30px;
}

.empty-icon {
  font-size: 5rem;
  color: #3f51b5;
  position: relative;
  z-index: 2;
  animation: float 3s ease-in-out infinite;
}

.empty-background-icon {
  position: absolute;
  font-size: 10rem;
  color: rgba(63, 81, 181, 0.1);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.empty-accompanists h3 {
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 15px;
}

.empty-accompanists > p {
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 25px;
  line-height: 1.6;
}

.empty-info-card {
  background-color: white;
  border-radius: 12px;
  padding: 20px;
  margin: 30px auto;
  max-width: 500px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 15px;
  text-align: left;
}

.info-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.info-content h4 {
  font-size: 1.1rem;
  color: #3f51b5;
  margin: 0 0 10px 0;
}

.info-content p {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

.goto-code-btn {
  background-color: #3f51b5;
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
}

.goto-code-btn:hover {
  background-color: #303f9f;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(63, 81, 181, 0.3);
}

/* Responsive pour les accompagnants */
@media (max-width: 768px) {
  .invitations-grid,
  .observers-grid {
    grid-template-columns: 1fr;
  }

  .invitation-actions {
    flex-direction: column;
  }

  .accept-btn,
  .reject-btn {
    width: 100%;
  }

  .empty-accompanists {
    padding: 40px 20px;
  }

  .empty-info-card {
    flex-direction: column;
    text-align: center;
  }
}

/* Impression du CV */
@media print {
  .profile-container {
    padding: 0;
    max-width: 100%;
  }

  .profile-header,
  .navigation-tabs,
  .accessibility-controls,
  .cv-customize,
  .cv-actions,
  .profile-guide {
    display: none !important;
  }

  .profile-content {
    margin: 0;
    padding: 0;
  }

  .profile-section {
    border: none;
    box-shadow: none;
    padding: 0;
    margin: 0;
  }

  .cv-preview {
    width: 100%;
    box-shadow: none;
  }
}

/* ========================================
   NOUVEAUX STYLES - SHOWCASE / PRÉSENTATION
   ======================================== */

.personal-showcase {
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: fadeIn 0.5s ease;
}

.identity-card-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 25px;
  align-items: start;
}

.identity-card {
  background-color: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  position: relative;
  transition: transform 0.3s ease;
}

.identity-card:hover {
  transform: translateY(-5px);
}

.card-header-bg {
  height: 120px;
  background: linear-gradient(135deg, #3f51b5, #5c6bc0, #2196f3);
  position: relative;
}

.card-header-bg::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: white;
  border-radius: 50% 50% 0 0 / 20px 20px 0 0;
  transform: scaleX(1.5);
}

.identity-content {
  padding: 0 30px 20px 30px;
  display: flex;
  gap: 30px;
  position: relative;
  margin-top: -60px; /* Chevauchement avec le header */
}

.avatar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.showcase-avatar {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  border: 6px solid white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  background-color: white;
  object-fit: cover;
}

.level-bubble {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background: linear-gradient(45deg, #ff9800, #f57c00);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 3px solid white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.level-label {
  font-size: 0.6rem;
  font-weight: bold;
  text-transform: uppercase;
}

.level-number {
  font-size: 1.2rem;
  font-weight: 800;
  line-height: 1;
}

.identity-details {
  flex: 1;
  padding-top: 60px; /* Espace pour s'aligner avec l'avatar qui remonte */
}

.user-name {
  font-size: 2rem;
  color: #333;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 15px;
  font-family: 'Comic Sans MS', 'Chalkboard SE', sans-serif; /* Style ludique */
}

.age-badge {
  background-color: #e3f2fd;
  color: #2196f3;
  font-size: 0.9rem;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: bold;
}

.xp-container {
  margin-bottom: 20px;
}

.xp-bar-fancy {
  height: 12px;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 5px;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

.xp-fill-fancy {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a, #cddc39);
  border-radius: 10px;
  transition: width 0.8s ease-out;
}

.xp-text {
  font-size: 0.85rem;
  color: #888;
  font-style: italic;
}

.bio-bubble {
  background-color: #f8f9fa;
  border-radius: 15px;
  padding: 15px;
  position: relative;
  border-left: 5px solid #3f51b5;
}

.bio-text {
  margin: 0;
  color: #555;
  line-height: 1.5;
  font-size: 1rem;
}

.edit-bio-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.edit-bio-btn:hover {
  opacity: 1;
}

/* Section Hobbies / Stickers */
.hobbies-section {
  padding: 0 30px 30px 30px;
}

.card-subtitle {
  font-size: 1.2rem;
  color: #3f51b5;
  margin-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 5px;
  display: inline-block;
}

.stickers-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  padding: 10px 0;
}

.sticker-item {
  background-color: #ffd54f;
  color: #333;
  font-weight: bold;
  padding: 8px 16px;
  border-radius: 4px;
  box-shadow: 2px 3px 5px rgba(0, 0, 0, 0.15);
  font-family: 'Comic Sans MS', cursive; /* Look sticker */
  border: 2px solid white;
  transform: rotate(-2deg);
  transition: transform 0.2s;
  cursor: default;
}

.sticker-item:hover {
  transform: scale(1.1) rotate(0deg) !important;
  z-index: 10;
}

.sticker-item:nth-child(even) {
  background-color: #4fc3f7;
  transform: rotate(2deg);
}

.sticker-item:nth-child(3n) {
  background-color: #81c784;
  transform: rotate(-1deg);
}

.add-sticker-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px dashed #ccc;
  background: none;
  color: #ccc;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.add-sticker-btn:hover {
  border-color: #3f51b5;
  color: #3f51b5;
  background-color: rgba(63, 81, 181, 0.05);
}

/* Code Ticket */
.code-ticket {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  position: sticky;
  top: 20px;
}

.ticket-stub {
  background-color: #333;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background-image: radial-gradient(circle at 10px 10px, rgba(255,255,255,0.1) 2px, transparent 2.5px);
  background-size: 20px 20px;
}

.ticket-stub::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  height: 20px;
  background: radial-gradient(circle, transparent 10px, white 10px);
  background-size: 30px 20px;
  background-position: center bottom;
}

.ticket-icon {
  font-size: 2rem;
}

.ticket-body {
  padding: 30px 20px 20px;
  text-align: center;
  border-bottom: 2px dashed #eee;
}

.ticket-label {
  display: block;
  font-size: 0.8rem;
  color: #888;
  letter-spacing: 1px;
  margin-bottom: 10px;
  font-weight: bold;
}

.ticket-code {
  font-family: 'Courier New', monospace;
  font-size: 2rem;
  font-weight: 900;
  color: #333;
  letter-spacing: 3px;
  margin-bottom: 15px;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 8px;
}

.ticket-action {
  background-color: #333;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.3s;
  width: 100%;
}

.ticket-action:hover {
  background-color: #555;
}

.ticket-action.copied {
  background-color: #4caf50;
}

.ticket-info {
  padding: 15px;
  background-color: #fafafa;
  color: #777;
  font-size: 0.85rem;
  text-align: center;
  font-style: italic;
}

/* Responsive pour la showcase */
@media (max-width: 900px) {
  .identity-card-container {
    grid-template-columns: 1fr;
  }
  
  .code-ticket {
    position: static;
    flex-direction: row;
    align-items: center;
  }
  
  .ticket-stub {
    width: 80px;
    height: auto;
    align-self: stretch;
    background-image: none;
    border-right: 2px dashed rgba(255,255,255,0.3);
  }
  
  .ticket-stub::after {
    display: none;
  }
  
  .ticket-body {
    padding: 15px;
    flex: 1;
    border-bottom: none;
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .ticket-code {
    margin-bottom: 5px;
    font-size: 1.5rem;
  }
  
  .ticket-action {
    width: auto;
    font-size: 0.8rem;
    padding: 6px 12px;
  }
  
  .ticket-info {
    display: none;
  }
}

@media (max-width: 600px) {
  .identity-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding-top: 0;
    margin-top: -60px;
  }
  
  .identity-details {
    padding-top: 10px;
    width: 100%;
  }
  
  .user-name {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .bio-bubble {
    text-align: left;
  }
  
  .hobbies-section {
    text-align: center;
  }
  
  .stickers-container {
    justify-content: center;
  }
  
  .code-ticket {
    flex-direction: column;
  }
  
  .ticket-stub {
    height: 50px;
    width: 100%;
  }
  
  .ticket-body {
    align-items: center;
    text-align: center;
  }
}

/* ========================================
   STYLES - COMPÉTENCES (GAMING / DASHBOARD)
   ======================================== */

.skills-showcase {
  background-color: #f0f2f5;
  border-radius: 20px;
  overflow: hidden;
  padding: 0;
  border: none;
  box-shadow: none;
}

.section-header-gaming {
  background: linear-gradient(135deg, #2c3e50, #4ca1af);
  padding: 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  border-radius: 20px 20px 0 0;
}

.gaming-title {
  margin: 0;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Comic Sans MS', 'Chalkboard SE', sans-serif;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.refresh-skills-btn-modern {
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.4);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.refresh-skills-btn-modern:hover:not(:disabled) {
  background: white;
  color: #2c3e50;
  transform: rotate(180deg);
}

.skills-dashboard {
  padding: 25px;
}

/* HUD (Heads-Up Display) Stats */
.skills-hud {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.hud-item {
  background: white;
  border-radius: 15px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  border-bottom: 4px solid #e0e0e0;
}

.hud-item.highlight {
  border-bottom-color: #ffd700;
  background: linear-gradient(to bottom, white, #fffde7);
}

.hud-value {
  font-size: 2rem;
  font-weight: 900;
  color: #333;
}

.hud-label {
  font-size: 0.85rem;
  color: #777;
  font-weight: bold;
  text-transform: uppercase;
}

/* Decks de Compétences */
.skill-decks-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.skill-deck {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.deck-title {
  margin: 0 0 20px 0;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #333;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 10px;
}

.deck-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

/* Carte de Compétence Moderne */
.skill-card-modern {
  display: flex;
  align-items: center;
  gap: 15px;
  background: #f9f9f9;
  border-radius: 12px;
  padding: 15px;
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid transparent;
}

.skill-card-modern:hover {
  transform: translateY(-3px);
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  background: white;
  border-color: #e0e0e0;
}

.skill-card-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  box-shadow: 0 3px 6px rgba(0,0,0,0.1);
  flex-shrink: 0;
}

.skill-card-content {
  flex: 1;
  min-width: 0; /* Pour que le text-overflow fonctionne */
}

.skill-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.skill-name {
  font-weight: 700;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 5px;
}

.skill-source-tag {
  font-size: 0.65rem;
  background: #e0e0e0;
  color: #666;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
}

/* Barres de niveau (Pips) */
.skill-level-bar-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.skill-level-pips {
  display: flex;
  gap: 3px;
}

.level-pip {
  width: 15px;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 2px;
  transition: background-color 0.3s;
}

.level-pip.max {
  box-shadow: 0 0 5px currentColor;
}

.skill-level-text {
  font-size: 0.75rem;
  font-weight: 700;
}

/* Widget Speedometer */
.speed-widget-container {
  margin-top: 30px;
  background: linear-gradient(135deg, #1a2980, #26d0ce);
  border-radius: 15px;
  padding: 25px;
  color: white;
  box-shadow: 0 10px 20px rgba(26, 41, 128, 0.3);
}

.widget-title {
  margin: 0 0 20px 0;
  font-size: 1.4rem;
  text-align: center;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.speed-widget {
  display: flex;
  align-items: center;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 20px;
}

.speed-gauge {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 8px solid rgba(255,255,255,0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
}

.gauge-circle {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 8px solid transparent;
  border-top-color: #ffd700;
  border-right-color: #ffd700;
  transform: rotate(calc(var(--percentage) * 3.6deg)); /* Simulation */
  transition: transform 1s ease-out;
  opacity: 0.8;
}

.gauge-value {
  font-size: 2.5rem;
  font-weight: 900;
  line-height: 1;
}

.gauge-unit {
  font-size: 0.8rem;
  opacity: 0.8;
}

.speed-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: rgba(0,0,0,0.2);
  padding: 15px;
  border-radius: 10px;
  min-width: 200px;
}

.speed-stat {
  display: flex;
  align-items: center;
  gap: 10px;
}

.speed-label {
  flex: 1;
  font-size: 0.9rem;
  opacity: 0.9;
}

.speed-data {
  font-weight: bold;
  font-size: 1.1rem;
  color: #ffd700;
}

.speed-message {
  margin-top: 10px;
  font-size: 0.9rem;
  font-style: italic;
  text-align: center;
  color: #b2dfdb;
}

.empty-deck {
  grid-column: 1 / -1;
  text-align: center;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 10px;
  color: #999;
}

.empty-deck-icon {
  font-size: 2rem;
  display: block;
  margin-bottom: 5px;
  opacity: 0.5;
}

/* Loading & Error Modern */
.loading-skills-modern {
  padding: 50px;
  text-align: center;
}

.loading-spinner-modern {
  width: 50px;
  height: 50px;
  border: 5px solid rgba(0,0,0,0.1);
  border-top-color: #3f51b5;
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin: 0 auto 20px;
}

.skills-error-modern {
  background: #ffebee;
  color: #c62828;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  margin: 20px;
}

.retry-btn {
  background: #c62828;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: pointer;
  margin-top: 10px;
}

/* Responsive Skills */
@media (max-width: 768px) {
  .skills-hud {
    grid-template-columns: 1fr;
  }
  
  .deck-cards {
    grid-template-columns: 1fr;
  }
  
  .speed-widget {
    flex-direction: column;
  }
  
  .section-header-gaming {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
}

/* ========================================
   STYLES - ENVIRONNEMENT (MON QG)
   ======================================== */

.environment-showcase {
  background-color: #f0f2f5;
  border-radius: 20px;
  overflow: hidden;
  padding: 0;
  border: none;
  box-shadow: none;
}

.hq-theme {
  background: linear-gradient(135deg, #37474f, #607d8b);
}

.hq-dashboard {
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Specs Card */
.hq-specs-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  border: 1px solid #e0e0e0;
}

.specs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px dashed #e0e0e0;
  padding-bottom: 10px;
}

.specs-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #333;
}

.specs-status {
  font-size: 0.7rem;
  font-weight: bold;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 4px 8px;
  border-radius: 4px;
  letter-spacing: 1px;
}

.specs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
}

.spec-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.spec-label {
  font-size: 0.9rem;
  color: #666;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
}

.spec-value-box {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  font-weight: bold;
  text-align: center;
  border-left: 4px solid #ccc;
  font-family: 'Courier New', monospace; /* Look technique */
}

.light-box { border-left-color: #ffb74d; }
.noise-box { border-left-color: #81c784; }
.social-box { border-left-color: #64b5f6; }
.comfort-box { border-left-color: #ba68c8; }

/* Locations Section */
.hq-locations-section {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.subsection-title-gaming {
  font-size: 1.3rem;
  color: #333;
  margin: 0;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.locations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 15px;
}

.location-card {
  background: white;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  gap: 15px;
  align-items: flex-start;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: transform 0.2s;
  border: 1px solid transparent;
}

.location-card:hover {
  transform: translateY(-3px);
  border-color: #b0bec5;
}

.location-icon-wrapper {
  width: 50px;
  height: 50px;
  background: #eceff1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.location-details {
  flex: 1;
}

.location-name {
  margin: 0 0 5px 0;
  font-size: 1.1rem;
  color: #333;
}

.location-badge {
  font-size: 0.75rem;
  background: #e0f2f1;
  color: #00695c;
  padding: 3px 8px;
  border-radius: 4px;
  font-weight: bold;
  display: inline-block;
  margin-bottom: 5px;
}

.location-notes {
  font-size: 0.85rem;
  color: #78909c;
  font-style: italic;
  line-height: 1.4;
}

/* Intel (Recommendations) */
.hq-intel-section {
  background: #fff;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.intel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 15px;
}

.intel-card {
  display: flex;
  gap: 15px;
  background: #fafafa;
  padding: 15px;
  border-radius: 10px;
  border-left: 3px solid #607d8b;
}

.intel-icon {
  font-size: 1.5rem;
}

.intel-content h4 {
  margin: 0 0 5px 0;
  font-size: 1rem;
  color: #37474f;
}

.intel-content p {
  margin: 0;
  font-size: 0.9rem;
  color: #546e7a;
  line-height: 1.4;
}

/* Action Button Gaming */
.action-button-gaming {
  background: #ff7043;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 50px;
  font-weight: bold;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 4px 0 #d84315;
  transition: all 0.1s;
  margin-top: 15px;
}

.action-button-gaming:active {
  transform: translateY(4px);
  box-shadow: 0 0 0 #d84315;
}

/* HQ Progress Bar */
.hq-progress-bar {
  background: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.progress-label {
  font-size: 0.85rem;
  font-weight: bold;
  color: #555;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.progress-track {
  height: 10px;
  background: #eceff1;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 5px;
}

.progress-fill-gaming {
  height: 100%;
  background: linear-gradient(90deg, #29b6f6, #26c6da);
  transition: width 0.5s ease;
}

.progress-value {
  text-align: right;
  font-size: 0.8rem;
  color: #78909c;
  font-weight: bold;
}

/* Responsive HQ */
@media (max-width: 768px) {
  .specs-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .intel-grid {
    grid-template-columns: 1fr;
  }
}

/* ========================================
   STYLES - MÉTIERS (MA COLLECTION)
   ======================================== */

.career-showcase {
  background-color: #f0f2f5;
  border-radius: 20px;
  overflow: hidden;
  padding: 0;
  border: none;
  box-shadow: none;
}

.career-theme {
  background: linear-gradient(135deg, #ff8f00, #ffb300);
}

.career-dashboard {
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* HUD Career */
.career-hud {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.highlight-liked {
  border-bottom-color: #ff6b6b !important;
  background: linear-gradient(to bottom, white, #fff5f5) !important;
}

/* Trading Cards */
.collection-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
  margin-top: 15px;
}

.job-trading-card {
  position: relative;
  height: 300px;
  cursor: pointer;
  perspective: 1000px;
  transition: transform 0.3s;
}

.job-trading-card:hover {
  transform: scale(1.05) translateY(-10px);
  z-index: 10;
}

.card-rarity-glow {
  position: absolute;
  top: -5%;
  left: -5%;
  width: 110%;
  height: 110%;
  background: radial-gradient(circle, rgba(255,143,0,0.3) 0%, transparent 70%);
  z-index: 1;
  border-radius: 20px;
  opacity: 0;
  transition: opacity 0.3s;
}

.job-trading-card:hover .card-rarity-glow {
  opacity: 1;
}

.card-inner {
  position: relative;
  height: 100%;
  background: white;
  border-radius: 15px;
  border: 4px solid #ffca28;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2;
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.card-visual {
  height: 50%;
  background: #fff8e1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 2px solid #ffca28;
  position: relative;
}

.job-emoji-large {
  font-size: 4rem;
}

.card-info {
  flex: 1;
  padding: 15px;
  text-align: center;
}

.card-title {
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  font-weight: 900;
  color: #333;
  text-transform: uppercase;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
}

.card-tag {
  background: #f5f5f5;
  color: #666;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: bold;
}

.card-footer {
  background: #ffca28;
  color: #333;
  padding: 8px;
  font-weight: 900;
  font-size: 0.8rem;
  letter-spacing: 1px;
}

/* Mission Log */
.mission-log-section {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.mission-log-container {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

.log-entry {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #ccc;
}

.log-entry.like { border-left-color: #4caf50; }
.log-entry.dislike { border-left-color: #f44336; }
.log-entry.unknown { border-left-color: #ff9800; }

.log-status-icon {
  font-size: 1.2rem;
}

.log-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.log-job-name {
  font-weight: bold;
  color: #333;
}

.log-date {
  font-size: 0.75rem;
  color: #999;
}

.log-result-tag {
  font-size: 0.7rem;
  font-weight: 900;
  padding: 4px 8px;
  border-radius: 4px;
  background: rgba(0,0,0,0.05);
  color: #666;
}

/* Career Intel */
.career-intel-section {
  background: #fff;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
}

.intel-stat-card {
  display: flex;
  align-items: center;
  gap: 15px;
  background: #fff8e1;
  padding: 15px;
  border-radius: 12px;
  border: 1px solid #ffe082;
}

.intel-stat-icon {
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}

.intel-stat-info {
  flex: 1;
}

.intel-stat-name {
  font-weight: bold;
  color: #333;
  font-size: 1rem;
}

.intel-stat-match {
  font-size: 0.85rem;
  color: #666;
}

.intel-stat-match strong {
  color: #ff8f00;
}

/* Responsive Career */
@media (max-width: 768px) {
  .career-hud {
    grid-template-columns: 1fr;
  }
  
  .collection-grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 480px) {
  .collection-grid {
    grid-template-columns: 1fr;
  }
}

/* ========================================
   STYLES - ACCOMPAGNANTS (MON ALLIANCE)
   ======================================== */

.team-showcase {
  background-color: #f0f2f5;
  border-radius: 20px;
  overflow: hidden;
  padding: 0;
  border: none;
  box-shadow: none;
}

.team-theme {
  background: linear-gradient(135deg, #1565c0, #1976d2);
}

.team-dashboard {
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Signal Alerts (Pending) */
.signal-alerts-section {
  background: #fff3e0;
  border-radius: 15px;
  padding: 20px;
  border-left: 5px solid #ff9800;
  animation: borderPulse 2s infinite;
}

@keyframes borderPulse {
  0% { border-left-color: #ff9800; }
  50% { border-left-color: #ffcc80; }
  100% { border-left-color: #ff9800; }
}

.subsection-title-gaming.alert {
  color: #e65100;
  animation: flash 1.5s infinite;
}

@keyframes flash {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.signals-container {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 15px;
}

.signal-card {
  background: white;
  border-radius: 12px;
  padding: 15px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
}

.signal-pulse {
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: #ff9800;
  animation: signalSlide 2s infinite linear;
}

@keyframes signalSlide {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
}

.signal-content {
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 2;
  position: relative;
}

.signal-avatar {
  width: 50px;
  height: 50px;
  background: #ff9800;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
}

.signal-info {
  flex: 1;
}

.signal-name {
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
}

.signal-meta {
  font-size: 0.85rem;
  color: #666;
}

.signal-actions {
  display: flex;
  gap: 10px;
}

.accept-signal-btn {
  background: #4caf50;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 900;
  font-size: 0.75rem;
  cursor: pointer;
}

.reject-signal-btn {
  background: #f44336;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 900;
  font-size: 0.75rem;
  cursor: pointer;
}

/* Squad Grid (Active) */
.squad-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.squad-count {
  background: #1976d2;
  color: white;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 0.9rem;
  margin-left: 10px;
}

.squad-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.member-dossier {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  border: 1px solid #e0e0e0;
  transition: transform 0.3s;
}

.member-dossier:hover {
  transform: translateY(-5px);
  border-color: #1976d2;
}

.member-header {
  height: 80px;
  background: #f5f7f9;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid #eee;
}

.member-avatar-glow {
  width: 60px;
  height: 60px;
  background: white;
  border-radius: 50%;
  padding: 3px;
  box-shadow: 0 0 15px rgba(25, 118, 210, 0.2);
  margin-top: 40px;
}

.member-avatar {
  width: 100%;
  height: 100%;
  background: #1976d2;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.4rem;
}

.member-status-tag {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: bold;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 4px 8px;
  border-radius: 4px;
  letter-spacing: 1px;
}

.member-body {
  padding: 40px 20px 20px;
}

.member-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: #333;
}

.member-role-badge {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: bold;
  color: #1976d2;
  background: #e3f2fd;
  padding: 3px 10px;
  border-radius: 20px;
  margin-top: 5px;
}

.member-stats {
  margin-top: 15px;
  display: flex;
  gap: 15px;
  border-top: 1px solid #f0f0f0;
  padding-top: 15px;
}

.m-stat {
  display: flex;
  flex-direction: column;
}

.m-label {
  font-size: 0.7rem;
  color: #999;
  text-transform: uppercase;
  font-weight: bold;
}

.m-value {
  font-size: 0.85rem;
  color: #555;
  font-weight: 600;
}

.member-intel {
  margin-top: 15px;
  background: #fafafa;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  gap: 10px;
  border-left: 3px solid #1976d2;
}

.intel-text {
  margin: 0;
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

/* Recruitment theme for empty deck */
.recruitment-theme {
  border: 2px dashed #1976d2 !important;
  background: #f0f7ff !important;
}

/* Responsive Team */
@media (max-width: 768px) {
  .signal-content {
    flex-direction: column;
    text-align: center;
  }
  
  .signal-actions {
    width: 100%;
  }
  
  .signal-actions button {
    flex: 1;
  }
  
  .squad-grid {
    grid-template-columns: 1fr;
  }
}
</style>
